import type { MessageKey } from './messages.en.ts';

/**
 * MVP-272 Phase 0. Every key in `messages.en.ts`, translated — no more, no fewer.
 *
 * The compile-time half of that guarantee comes from two things stacking: `Record<MessageKey,
 * string>` requires every key `en` has (delete one here and this fails to compile), and assigning
 * this object as a fresh literal — not built up via spread or a computed key, either of which
 * would defeat it — gets TypeScript's excess-property check on top (add a key `en` does not have
 * and this also fails to compile). `i18n.test.ts` asserts the same thing again at runtime via
 * `Object.keys` on both modules, because the type checks only catch a mismatch introduced by
 * editing this literal by hand — they cannot catch one introduced by a build step or generator
 * that reads a stale copy of one file and not the other.
 *
 * These are placeholder translations for Phase 0's proof-of-mechanism keys only (see
 * `messages.en.ts`'s header) — not yet reviewed by the owner, like every other string this task
 * will eventually add to this file.
 */
export const th: Record<MessageKey, string> = {
  'common.cancel': 'ยกเลิก',
  'common.retry': 'ลองอีกครั้ง',
  'greeting.welcome': 'ยินดีต้อนรับ {name}',

  // MVP-293: the password reveal toggle. Only a screen reader ever reads these — the control itself
  // is an icon — so they are stated as the action pressing performs, not as a label for the eye.
  'field.revealPassword': 'แสดงรหัสผ่าน',
  'field.hidePassword': 'ซ่อนรหัสผ่าน',

  // MVP-272 Phase 1 (guest request page). Owner-approved 2026-09-07 — plain, warm Thai for an
  // ordinary bar customer on their own phone, "พนักงาน" (staff) standing in for "the KJ" throughout
  // since that acronym has no Thai equivalent in the approved glossary.
  'guest.gate.wrongPasscode': 'รหัสผ่านของร้านไม่ถูกต้อง กรุณาถามพนักงาน',
  'guest.gate.linkExpiredTitle': 'ลิงก์นี้ใช้งานไม่ได้แล้ว',
  'guest.gate.linkExpiredDetail': 'กรุณาขอ QR โค้ดหรือลิงก์ใหม่จากพนักงาน',
  'guest.gate.passcodeTitle': 'งานนี้ต้องใช้รหัสผ่าน',
  'guest.gate.passcodeFallbackDescription': 'กรุณาถามรหัสผ่านของคืนนี้จากพนักงาน',
  'guest.gate.passcodeLabel': 'รหัสผ่าน',
  'guest.gate.continue': 'ต่อไป',
  // MVP-471. Not yet owner-reviewed.
  'guest.gate.challengeTitle': 'ยืนยันก่อนสักนิด',
  'guest.gate.challengeDescription': 'แตะช่องด้านล่างเพื่อยืนยันว่าไม่ใช่บอท ทำครั้งเดียวพอสำหรับคืนนี้',
  'guest.gate.challengeChecking': 'กำลังตรวจสอบ…',
  'guest.gate.challengeUnavailable': 'โหลดการยืนยันไม่สำเร็จ ลองปิดตัวบล็อกโฆษณา หรือแจ้งพนักงาน',
  'guest.pageTitle': 'ขอเพลง',
  'guest.closed.title': 'ขณะนี้ปิดรับคำขอเพลง',
  'guest.closed.fallbackDescription': 'กรุณากลับมาดูอีกครั้งเร็ว ๆ นี้',

  // MVP-274 R1. "รหัสเพลง" is what the number in the venue's book is called on the book itself and
  // by the KJ over the mic — never "หมายเลขแคตตาล็อก", which is warehouse Thai for the same thing.
  'guest.find.modeLabel': 'เลือกวิธีหาเพลง',
  'guest.find.modeCode': 'รหัสเพลง',
  'guest.find.modeSearch': 'ค้นหา',
  'guest.find.browseNote': 'เพลงทั้งหมด เรียงตามรหัส แตะเลือกได้เลย หรือกดรหัสด้านบน',
  // MVP-451. Not yet owner-reviewed.
  'guest.find.loadingMore': 'กำลังโหลดเพลงเพิ่ม…',
  'guest.code.hint': 'กดรหัสเพลงจากในเล่ม',
  'guest.code.enteredLabel': 'รหัสเพลงที่กด',
  'guest.code.clear': 'ล้างรหัส',
  'guest.code.backspace': 'ลบตัวเลขตัวสุดท้าย',
  'guest.code.noMatchTitle': 'ไม่มีเพลงรหัส {code}',
  'guest.code.noMatchHint': 'ลองดูรหัสในเล่มอีกครั้ง หรือค้นหาจากชื่อเพลงแทน',
  'guest.code.emptyTitle': 'ยังไม่มีเพลงให้แสดง',
  'guest.code.emptyHint': 'กดรหัสเพลงด้านบน หรือถามพนักงานได้',

  'guest.search.label': 'ค้นหาเพลง',
  'guest.search.placeholder': 'ค้นหาจากชื่อเพลงหรือชื่อศิลปิน',
  'guest.search.noMatchesTitle': 'ไม่พบเพลงที่ตรงกับ "{query}"',
  'guest.search.emptyTitle': 'ค้นหาเพลง',
  'guest.search.noMatchesHint': 'ลองพิมพ์คำอื่นหรือชื่อศิลปินอื่นดูนะ',
  'guest.search.emptyHint': 'พิมพ์ชื่อเพลงหรือชื่อศิลปินเพื่อเริ่มค้นหา',
  'guest.search.clear': 'ล้างคำค้นหา',
  // MVP-335: Thai has no plural forms, so unlike the English pair above these two read almost the
  // same — the count itself is the only thing that changes — but they stay two keys because the
  // English side needs "song"/"songs" and a parity test enforces one key set across both files.
  'guest.search.resultsAnnouncementNone': 'ไม่พบเพลงที่ตรงกัน',
  'guest.search.resultsAnnouncementOneFound': 'พบเพลง 1 เพลง',
  'guest.search.resultsAnnouncementManyFound': 'พบเพลง {count} เพลง',

  'guest.songRow.unknownArtist': 'ไม่ทราบชื่อศิลปิน',
  'guest.songRow.catalogueCode': 'รหัสเพลง {code}',
  'guest.songRow.requestWithArtist': 'ขอเพลง {title} ของ {artist}',
  'guest.songRow.requestTitleOnly': 'ขอเพลง {title}',

  'guest.queue.songFallback': 'เพลงของคุณ',
  'guest.queue.askedPrefix': 'ขอไว้เมื่อ {age}',
  'guest.queue.dismiss': 'ปิด',

  'guest.queue.pending': 'รอพนักงานตอบรับ',
  'guest.queue.nowPlaying': 'ถึงคิวคุณแล้ว ขึ้นร้องได้เลย! \u{1F3A4}',
  'guest.queue.inLine': 'คิวที่ {position} · อีก {eta}',
  'guest.queue.acceptedFallback': 'รับเรื่องแล้ว ถามพนักงานเรื่องคิวของคุณได้เลย',
  'guest.queue.played': 'ร้องเพลงนี้จบแล้ว ขอบคุณที่ร่วมร้องเพลง',
  'guest.queue.rejectedWithReason': 'ครั้งนี้ไม่ได้: {reason}',
  'guest.queue.rejectedFallback': 'ครั้งนี้ไม่ได้ ถามพนักงานได้ว่าทำไม',
  'guest.queue.expired': 'คำขอเพลงนี้หมดเวลาก่อนพนักงานจะดำเนินการ ลองค้นหาใหม่อีกครั้งถ้ายังอยากร้องเพลงนี้',

  'guest.queue.age.unknown': '—',
  'guest.queue.age.justNow': 'เมื่อสักครู่',
  'guest.queue.age.minutes': '{n} นาทีที่แล้ว',
  'guest.queue.age.hours': '{n} ชั่วโมงที่แล้ว',
  'guest.queue.age.yesterday': 'เมื่อวาน',
  'guest.queue.age.days': '{n} วันที่แล้ว',

  'guest.rateLimit.tryAgainIn': 'ลองใหม่อีกครั้งใน {duration}',

  'guest.sheet.titleFallback': 'ขอเพลงนี้',
  'guest.sheet.submit': 'ขอเพลงนี้',
  'guest.sheet.keyChangeLabel': 'เปลี่ยนคีย์',
  'guest.sheet.keyChangeUnit': 'ครึ่งเสียง',
  'guest.sheet.close': 'ปิด',
  'guest.sheet.keyChangeDown': 'ลดคีย์',
  'guest.sheet.keyChangeUp': 'เพิ่มคีย์',

  'guest.singerName.label': 'ชื่อของคุณ',
  'guest.singerName.placeholder': 'อยากให้พนักงานเรียกคุณว่าอะไร',

  'guest.errors.codeRequired': 'ไม่มีรหัสเข้าร่วม',
  'guest.errors.sessionNotFound': 'ไม่พบงานสำหรับรหัสนี้',
  'guest.errors.sessionNotFoundDetail': 'กรุณาตรวจสอบรหัสบนหน้าจอ หรือถามพนักงาน',
  'guest.errors.requestsClosed': 'ปิดรับคำขอเพลงแล้ว',
  'guest.errors.passcodeInvalid': 'รหัสผ่านไม่ถูกต้อง',
  'guest.errors.songRequired': 'กรุณาเลือกเพลงก่อน',
  'guest.errors.deviceIdRequired': 'ไม่สามารถระบุตัวตนโทรศัพท์เครื่องนี้ได้',
  'guest.errors.singerNameRequired': 'กรุณากรอกชื่อของคุณ',
  'guest.errors.singerNameInvalid': 'ชื่อนี้ใช้ไม่ได้',
  'guest.errors.duplicateRequest': 'อยู่ในรายการแล้ว',
  'guest.errors.deviceBlocked': 'คุณถูกระงับการขอเพลง',
  'guest.errors.rateLimited': 'ช้าลงหน่อยนะ',
  'guest.errors.requestsFull': 'คำขอเพลงเต็มแล้วตอนนี้',
  'guest.errors.networkError': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้',
  'guest.errors.genericTitle': 'ส่งคำขอไม่สำเร็จ',
  'guest.errors.genericDetail': 'การเชื่อมต่อขัดข้อง กรุณาตรวจสอบสัญญาณแล้วลองใหม่อีกครั้ง',
  'guest.errors.tryAgainDetail': 'กรุณาลองใหม่อีกครั้งในอีกสักครู่',
  'guest.errors.serverMessage': '{message}',
  'guest.errors.alreadyInQueue': 'อยู่ในคิวแล้ว',
  'guest.errors.duplicateNowPlaying': 'ถึงคิวคุณแล้ว',
  'guest.errors.duplicateAccepted': 'รับเรื่องแล้ว',
  'guest.errors.duplicatePlayed': 'ร้องเพลงนี้ไปแล้ว',
  'guest.errors.duplicateAsked': 'ขอเพลงนี้ไปแล้ว',

  // MVP-272 Phase 2 (Stage). Read from across a room, so these run shorter than the guest page's
  // copy — a sentence that works on a phone held at arm's length is a paragraph at eight metres.
  'common.unknownArtist': 'ไม่ทราบชื่อศิลปิน',
  'common.unknownSong': 'ไม่ทราบชื่อเพลง',
  'common.mediaLookupFailed': 'โหลดไฟล์สื่อของ "{title}" ไม่สำเร็จ',

  'stage.documentTitle': 'จอเวที — RMS Karaoke',
  'stage.gesture': 'คลิกที่ใดก็ได้เพื่อเริ่มจอเวที',
  'stage.joinBadge': 'ขอเพลง',
  'stage.paused': 'พักภาพ',
  'stage.breakMusic': 'เพลงพัก',

  'stage.mediaError': 'เล่นไฟล์ไม่สำเร็จ',
  'stage.mediaErrorWithCode': 'เล่นไฟล์ไม่สำเร็จ (รหัส {code})',

  'stage.hold.errorHeadline': 'รอสักครู่',
  'stage.hold.errorDetail': 'การเล่นถูกขัดจังหวะ',
  'stage.hold.limbo': 'กำลังเตรียมเพลงถัดไป…',
  'stage.hold.mediaMissing': 'ตอนนี้เพลงนี้เล่นไม่ได้',

  'stage.countdown.label': 'เพลงถัดไปในอีก',
  'stage.upNext.label': 'เพลงถัดไป',

  'stage.attract.comingUp': 'คิวถัดไป',
  'stage.attract.unnamedSinger': 'ไม่ระบุชื่อ',
  'stage.attract.joinHeadline': 'ขอเพลงจากมือถือของคุณได้เลย',
  // "cockpit" has no settled Thai term in the approved glossary yet — the operator nav still reads
  // "Cockpit" in English today. "หน้าควบคุม" is provisional; MVP-272 Phase 3 translates that screen
  // and owns the decision, and this string should follow whatever it picks.
  'stage.attract.noSessionHeadline': 'จอเวทีพร้อมแล้ว เริ่มงานได้จากหน้าควบคุม',

  'stage.marquee.announcement': 'ประกาศ',

  // MVP-272 Phase 3a (cockpit transport bar). Operator Thai, not customer Thai: the reader here is
  // the KJ working the machine, so the loan words they already say out loud in a Thai karaoke bar
  // stay in English — "คีย์" (key), "ซิงค์" (sync), "L"/"R", "A"/"B" — except tempo, which
  // MVP-680 made "จังหวะ" at the owner's word. Translating
  // those into literary Thai would be a glossary nobody at the desk uses. Everything that is a
  // SENTENCE — a hint, a state, a warning — is Thai.
  //
  // NOT yet reviewed by the owner (same standing caveat as every phase before this one); the
  // provisional "หน้าควบคุม" for "cockpit" that Phase 2 flagged is still unresolved and no string
  // below needs to say the word, so this phase does not settle it either.
  'player.resetToZero': 'รีเซ็ตเป็น 0',

  // MVP-375.
  // MVP-410. Not yet owner-reviewed. Both sentences now name the group ('ปุ่มควบคุมเพลง') instead of
  // saying "these buttons", which covered three controls that never go dark.
  'player.controls.noSong': 'เริ่มเพลงจากคิวก่อน ถึงจะใช้ปุ่มควบคุมเพลงได้',
  // MVP-398. Not yet owner-reviewed: "รอบร้อง" is the glossary's word for the rotation, and this
  // is the first string to ask the KJ to put a person into it rather than a song into a queue.
  'player.controls.noRotation': 'เพิ่มผู้ร้องเข้ารอบร้องก่อน ถึงจะใช้ปุ่มควบคุมเพลงได้',
  // MVP-384.
  'player.controls.groupLabel': 'ปุ่มควบคุมเพลง',
  // MVP-558. Not yet owner-reviewed.
  'player.controls.audioPathLabel': 'เส้นทางเสียงของห้อง',

  'player.key.trigger': 'คีย์ {value}',
  'player.key.ariaLabel': 'ปรับคีย์ ตอนนี้ {value}',
  'player.key.popoverTitle': 'คีย์ (ครึ่งเสียง)',
  'player.key.stepperLabel': 'ปรับคีย์',

  // MVP-680, owner: "เปลี่ยนคำว่า เทมโป้ เป็น จังหวะ". The loan-word rule above stands for the rest
  // of the row — "คีย์" and "ซิงค์" are what the KJ says out loud — and the owner has ruled that
  // tempo is not one of them. All four strings move together: a trigger reading "จังหวะ" beside a
  // popover titled "เทมโป้" is two names for one control.
  'player.tempo.trigger': 'จังหวะ {value}',
  'player.tempo.ariaLabel': 'จังหวะ ตอนนี้ {value}',
  'player.tempo.popoverTitle': 'จังหวะ',
  'player.tempo.stepperLabel': 'จังหวะ',

  // MVP-413. Not yet owner-reviewed.
  'player.volume.trigger': 'เสียง {value}%',
  'player.volume.triggerMuted': 'ปิดเสียงอยู่',
  'player.volume.ariaLabel': 'ระดับเสียง ตอนนี้ {value} เปอร์เซ็นต์',
  'player.volume.ariaLabelMuted': 'ระดับเสียง ตอนนี้ปิดอยู่',
  'player.volume.popoverTitle': 'ระดับเสียง',
  'player.volume.stepperLabel': 'ระดับเสียง',
  'player.volume.mute': 'ปิดเสียง',
  'player.volume.unmute': 'เปิดเสียง',
  'player.volume.reset': 'กลับไป 100%',
  'player.volume.decrease': 'ลดเสียง',
  'player.volume.increase': 'เพิ่มเสียง',

  'player.sync.trigger': 'ซิงค์ {value}',
  'player.sync.popoverTitle': 'ชดเชยเวลาเนื้อเพลงกับเสียง',
  'player.sync.stepperLabel': 'ชดเชยเวลา',
  'player.sync.hotkeyHint': 'คีย์ลัด: [ / ] · เลื่อนเนื้อเพลงเร็วขึ้นหรือช้าลง ไม่ใช่ตัวเสียง',

  'player.vocal.groupLabel': 'ช่องเสียง',
  'player.vocal.pickLeft': 'L',
  'player.vocal.pickRight': 'R',
  'player.vocal.pickBoth': 'ทั้งคู่',
  'player.vocal.guideVocalOn': 'เสียงร้องไกด์อยู่ช่อง {side}',
  // MVP-657. Not yet owner-reviewed. ใช้คำเดิมของแอป: "เสียงร้องไกด์" = guide vocal, "ช่อง" = channel
  // (ดู player.vocal.guideVocalOn, player.vocal.groupLabel)
  'player.vocal.tagLabel': 'เสียงร้องไกด์ของเพลงนี้',
  'player.vocal.tagUnknown': 'ยังไม่ระบุ',
  'player.vocal.tagLeft': 'ช่องซ้าย',
  'player.vocal.tagRight': 'ช่องขวา',
  'player.vocal.tagBoth': 'ทั้งสองช่อง',
  'player.vocal.tagNone': 'ไม่มีช่องเสียงร้องแยก',
  'player.vocal.tagSaved': 'บันทึกลงเพลงนี้แล้ว',
  'player.vocal.tagError': 'บันทึกช่องเสียงร้องไกด์ไม่สำเร็จ',
  'player.vocal.leftLevel': 'ระดับเสียงช่องซ้ายของต้นฉบับ',
  'player.vocal.rightLevel': 'ระดับเสียงช่องขวาของต้นฉบับ',

  // MVP-499, reviewed with the owner 2026-09-19 (MVP-675). ใช้คำเดิมของแอป: "เสียงร้องไกด์" = guide vocal, "ช่อง" = channel,
  // "tag" คงคำอังกฤษไว้เพราะหน้า catalog/cockpit ใช้คำนี้อยู่แล้ว (ดู MVP-657/658)
  'player.autoDuck.off': 'หรี่อัตโนมัติ: ปิด',
  'player.autoDuck.starting': 'กำลังเปิดไมค์…',
  'player.autoDuck.listening': 'กำลังฟัง',
  'player.autoDuck.ducking': 'กำลังหรี่เสียงร้องไกด์',
  'player.autoDuck.noChannel': 'เพลงนี้ยังไม่ได้ tag',
  // MVP-664, reviewed 2026-09-19 (MVP-675). "ทั้งคู่" is VocalChannelControl's own word for the Both pick.
  'player.autoDuck.isolated': 'ต้องเลือกช่องทั้งคู่',
  'player.autoDuck.denied': 'ไม่ได้รับอนุญาตให้ใช้ไมค์',
  'player.autoDuck.unavailable': 'หาไมค์ไม่เจอ',
  // MVP-667, reviewed 2026-09-19 (MVP-675).
  'settings.autoDuck': 'หรี่เสียงร้องไกด์อัตโนมัติ',
  'settings.autoDuck.note':
    'หรี่เสียงร้องไกด์ตอนมีคนร้อง แต่ละเครื่องยังเปิด/ปิดเองจากหน้า cockpit ได้ และค่าที่เครื่องตั้งเองจะชนะค่านี้',
  'settings.autoDuck.enabledByDefault': 'เปิดเป็นค่าเริ่มต้นทุกห้อง',
  'settings.autoDuck.sensitivity': 'ความไว',
  'settings.autoDuck.sensitivityHigh': 'ไว — จับเสียงร้องเบาๆ ได้',
  'settings.autoDuck.sensitivityNormal': 'ปกติ',
  'settings.autoDuck.sensitivityLow': 'ไม่ไว — เฉพาะเสียงร้องดัง',
  'settings.autoDuck.sensitivityNote':
    'ลดลงถ้าเสียงร้องไกด์หรี่เองทั้งที่ไม่มีใครร้อง เพิ่มขึ้นถ้าคนร้องเบาแล้วไม่หรี่ให้',

  // MVP-669, reviewed 2026-09-19 (MVP-675).
  'settings.autoDuck.depth': 'หรี่ลงแค่ไหน',
  'settings.autoDuck.depthLight': 'นิดเดียว — ยังได้ยินเสียงร้องไกด์ชัด',
  'settings.autoDuck.depthNormal': 'ปกติ',
  'settings.autoDuck.depthDeep': 'เยอะ — เสียงร้องไกด์แทบหาย',
  'settings.autoDuck.depthNote':
    'ไม่ปิดเสียงร้องไกด์สนิท เพลงที่ tag ผิดจึงฟังแปลกได้ แต่ไม่มีทางเสียดนตรีพื้นหลังไป',

  // MVP-683. คำที่ผู้ใช้เห็นพูดถึงสิ่งที่ได้ยิน ไม่ใช่ dB: KJ ตัดสินจากห้อง ไม่ได้ตัดสินจากตัวเลข
  // ("ดันขึ้นแรง" ที่ +6 dB อาจ clip กับ master ที่ดังอยู่แล้ว — note บอกไว้ตรงๆ)
  'settings.autoDuck.lift': 'ดันเสียงดนตรีขึ้นแค่ไหน',
  'settings.autoDuck.liftOff': 'ไม่ดัน — หรี่อย่างเดียว',
  'settings.autoDuck.liftNormal': 'ปกติ',
  'settings.autoDuck.liftStrong': 'แรง — ห้องที่เปิด PA ไม่ดังมาก',
  'settings.autoDuck.liftNote':
    'ตอนหรี่เสียงร้องไกด์ลง จะดันอีกช่องขึ้นแทน ห้องจะได้ไม่เงียบลงตอนมีคนร้อง เลือก "แรง" เฉพาะห้องที่ยังมีหัวเสียงเหลือ ไม่งั้นเพลงดังๆ อาจแตก',

  'player.autoDuck.hint':
    'ตอนมีคนร้อง จะหรี่เสียงร้องไกด์ลงและดันเสียงดนตรีขึ้น เฉพาะเพลงที่รู้ว่าเสียงร้องอยู่ช่องไหน ต้องเสียบไมค์เข้าเครื่องนี้',

  'player.abRepeat.triggerLabel': 'เล่นซ้ำ A-B',
  'player.abRepeat.popoverTitle': 'เล่นซ้ำ A–B',
  'player.abRepeat.setA': 'ตั้ง A',
  'player.abRepeat.setAPending': 'ตั้ง A ({seconds} วิ)',
  'player.abRepeat.setB': 'ตั้ง B',
  'player.abRepeat.clear': 'ล้างช่วงเล่นซ้ำ A-B',
  'player.abRepeat.looping': 'เล่นซ้ำ {a} วิ – {b} วิ',
  'player.abRepeat.prewarming': 'กำลังเตรียมจุดเริ่มของช่วงเล่นซ้ำ รอสักครู่',

  'player.scrub.seek': 'เลื่อนตำแหน่งเพลง',
  'player.scrub.valueText': '{position} จาก {duration} วินาที',
  'player.scrub.markerA': 'A: {seconds} วิ',
  'player.scrub.markerB': 'B: {seconds} วิ',

  'player.badge.breakMusic': 'เพลงคั่น',
  'player.badge.leftOnly': 'เฉพาะช่องซ้าย',
  'player.badge.rightOnly': 'เฉพาะช่องขวา',

  'player.buffering.buffering': 'กำลังโหลด…',
  'player.buffering.retrying': 'เล่นสะดุด กำลังลองใหม่ ({attempt})…',
  'player.buffering.upNext': 'เพลงถัดไป: {title} — กำลังโหลด…',
  'player.buffering.rate': '{mbps} Mbps',
  'player.buffering.eta': 'เหลืออีก ~{seconds} วิ',

  'player.error.playFailed': 'เล่น "{title}" ไม่ได้: {reason}',
  'player.error.unknownTrack': 'เพลงนี้',
  'player.error.advanceNothingQueued': '{reason} — ไม่มีเพลงถัดไปในคิว',
  'player.error.noMediaFile': '"{title}" ไม่มีไฟล์สื่อแนบอยู่ แจ้ง KJ ให้ ingest เพลงนี้ใหม่',
  'player.error.playbackFailed': 'เริ่มเพลงนี้ไม่ได้ ลองกดเล่นอีกครั้ง',
  'player.error.reason.aborted': 'การเล่นถูกขัดจังหวะ',
  'player.error.reason.network': 'การเชื่อมต่อหลุด',
  'player.error.reason.decode': 'ไฟล์เสียหาย',
  'player.error.reason.unsupported': 'เล่นไฟล์รูปแบบนี้ไม่ได้',
  'player.error.reason.notFound': 'ไม่พบไฟล์',
  'player.error.reason.serverError': 'เซิร์ฟเวอร์สื่อไม่ตอบสนอง',
  'player.error.reason.other': 'ไม่ทราบสาเหตุ',
  'player.error.reason.corsBlocked': 'เซิร์ฟเวอร์สื่อยังตั้งค่าไม่ถูกต้อง',

  // MVP-272 Phase 3b (rotation panel + request tray). Same operator Thai as 3a. "รอบ" is the
  // rotation itself (a turn coming back around), "คิว" is the songs queued inside one singer's
  // turn — the panel shows both at once and the two words are what keep them apart.
  // Not yet owner-reviewed.
  'rotation.title': 'รอบร้อง',
  'rotation.unknownSinger': 'ไม่ทราบชื่อผู้ร้อง',
  'rotation.thisSinger': 'ผู้ร้องคนนี้',
  'rotation.thisSong': 'เพลงนี้',

  // MVP-405, owner-reviewed 2026-09-13. Bare 'พัก' used to be all three of these at once, on
  // screens a KJ reads together: a held rotation slot, the transport's Pause, and a video paused
  // mid-performance on the audience screen. They stay one word-family — the KJ's own word — with
  // the object attached, so each says what is being held: 'พักคิว' (this singer's turn),
  // 'พักเพลง' (the transport, see `player.transport.pause`), 'พักภาพ' (the Stage, see
  // `stage.paused`). None of them is 'หยุด', which is `player.transport.stop` and means stopped.
  'rotation.badge.playing': 'กำลังเล่น',
  'rotation.badge.paused': 'พักคิว',
  'rotation.badge.upNext': 'คิวถัดไป',
  'rotation.badge.loading': 'กำลังโหลด…',

  'rotation.undo': 'เลิกทำ',
  'rotation.redo': 'ทำซ้ำ',
  'rotation.undoLabel': 'เลิกทำรายการล่าสุด',
  'rotation.undoNothing': 'ไม่มีอะไรให้เลิกทำ',
  'rotation.redoLabel': 'ทำซ้ำรายการที่เพิ่งเลิกทำ',
  'rotation.redoNothing': 'ไม่มีอะไรให้ทำซ้ำ',
  'rotation.undid': 'เลิกทำแล้ว: {op}',
  'rotation.redid': 'ทำซ้ำแล้ว: {op}',

  'rotation.working': 'กำลังบันทึก…',

  'rotation.startNext': 'เริ่มคิวถัดไป',
  // MVP-399. Not yet owner-reviewed: 'รอบร้อง' is the glossary's word for the rotation.
  'rotation.addSingerLabel': 'เพิ่มผู้ร้อง',
  'rotation.addSingerTitle': 'เพิ่มผู้ร้องเข้ารอบร้อง',
  'rotation.addedSinger': 'เพิ่ม {name} แล้ว',
  'rotation.startNextNoPlayback': 'ยังไม่ได้เชื่อมกับตัวเล่น',
  'rotation.startNextNothingQueued': 'ไม่มีเพลงในคิว',

  'rotation.stale': 'ข้อมูลรอบร้องอาจไม่ใช่ล่าสุด — อัปเดตเมื่อ {age} ที่แล้ว และเพิ่งรีเฟรชไม่สำเร็จ ({error})',
  'rotation.errorDetail': '{reason}: {message}',

  'rotation.loadFailedTitle': 'โหลดรอบร้องไม่สำเร็จ',
  'rotation.notLoadedTitle': 'ยังไม่ได้โหลดรอบร้อง',
  'rotation.notLoadedDescription': 'อ่านรอบร้องของคืนนี้ไม่ได้',
  'rotation.emptyTitle': 'ยังไม่มีใครในรอบร้อง',
  'rotation.noSongsQueued': 'ยังไม่มีเพลงในคิว',

  // MVP-550. Not yet owner-reviewed. 'รอบร้อง' is the glossary's word for the rotation; the box
  // narrows the list already on screen ('หา…ในรอบร้อง'), which is a different act from the walk-up
  // dialog's search over the whole singer directory.
  'rotation.searchLabel': 'หาผู้ร้องในรอบร้อง',
  'rotation.searchPlaceholder': 'ชื่อผู้ร้อง',
  'rotation.searchClear': 'ล้างคำค้นหา',
  'rotation.searchNoMatchTitle': 'ไม่มีใครในรอบร้องตรงกับ “{query}”',
  'rotation.filterHidesPlaying': '{name} กำลังร้องอยู่ — คำค้นหาซ่อนการ์ดของเขาไว้',

  // MVP-364: see the same block in messages.en.ts. The long form is the assistive-tech name, the
  // short one is the visible label beside the number.
  'rotation.songsThisRotation': 'จำนวนเพลงที่ร้องไปในรอบนี้',
  'rotation.songsFactLabel': 'เพลง',
  'rotation.etaHint': 'เวลาโดยประมาณจนถึงคิวของผู้ร้องคนนี้',
  'rotation.etaFactLabel': 'ถึงคิวใน',
  'rotation.pauseSinger': 'พักคิวของ {name}',
  'rotation.resumeSinger': 'เล่นคิวของ {name} ต่อ',
  'rotation.moveSlotUp': 'เลื่อนคิวผู้ร้องขึ้น',
  'rotation.moveSlotDown': 'เลื่อนคิวผู้ร้องลง',
  'rotation.removeSinger': 'เอาผู้ร้องออกจากรอบร้อง',
  'rotation.removedSinger': 'เอา {name} ออกแล้ว',
  'rotation.removedSong': 'เอา “{title}” ออกแล้ว',
  // MVP-412 (SPEC AC-Q-5). "ขาประจำ" is the venue's own word for a regular; the label says what
  // the toggle does (จำรายการเพลง) rather than naming the status, so the heart needs no legend.
  // Not yet owner-reviewed.
  'rotation.markRegular': 'จำรายการเพลงของ {name} ไว้ใช้คืนหน้า',
  'rotation.unmarkRegular': 'เลิกจำรายการเพลงของ {name}',
  'rotation.markedRegular': 'จำรายการเพลงของ {name} แล้ว',
  'rotation.unmarkedRegular': 'เลิกจำรายการเพลงของ {name} แล้ว',

  'rotation.error.undo': 'เลิกทำไม่สำเร็จ',
  'rotation.error.redo': 'ทำซ้ำไม่สำเร็จ',
  'rotation.error.reorderRotation': 'จัดลำดับรอบร้องไม่สำเร็จ',
  'rotation.error.reorderSongs': 'จัดลำดับเพลงของผู้ร้องคนนี้ไม่สำเร็จ',
  'rotation.error.pauseSinger': 'พักคิวของ {name} ไม่สำเร็จ',
  'rotation.error.resumeSinger': 'เล่นคิวของ {name} ต่อไม่สำเร็จ',
  'rotation.error.removeSinger': 'เอา {name} ออกไม่สำเร็จ',
  'rotation.error.markRegular': 'เริ่มจำรายการเพลงของ {name} ไม่สำเร็จ',
  'rotation.error.unmarkRegular': 'เลิกจำรายการเพลงของ {name} ไม่สำเร็จ',
  'rotation.error.addSinger': 'เพิ่ม {name} ไม่สำเร็จ',
  'rotation.error.removeSong': 'เอา "{title}" ออกไม่สำเร็จ',
  'rotation.error.keyShift': 'เปลี่ยนคีย์ไม่สำเร็จ',
  'rotation.error.playNow': 'เล่น "{title}" ตอนนี้ไม่สำเร็จ',
  'rotation.error.startNext': 'เริ่มเพลงถัดไปไม่สำเร็จ',

  'rotation.op.addSong': 'เพิ่มเพลง',
  'rotation.op.removeSong': 'เอาเพลงออก',
  'rotation.op.addSinger': 'เพิ่มผู้ร้อง',
  'rotation.op.removeSinger': 'เอาผู้ร้องออก',
  'rotation.op.reordered': 'จัดลำดับใหม่',
  'rotation.op.updateSinger': 'แก้ข้อมูลผู้ร้อง',
  'rotation.op.advanced': 'เดินรอบร้องต่อ',

  'rotation.row.moveUp': 'เลื่อนขึ้น',
  'rotation.row.moveDown': 'เลื่อนลง',
  'rotation.row.playNow': 'เล่นตอนนี้',
  'rotation.row.remove': 'เอาออกจากคิว',
  'rotation.row.playNowDisabled': 'เล่นตอนนี้ — {reason}',
  'rotation.row.removeDisabled': 'เอาออกจากคิว — {reason}',
  'rotation.reason.currentlyPlaying': 'กำลังเล่นอยู่',
  'rotation.reason.alreadyPlaying': 'กำลังเล่นอยู่แล้ว',
  'rotation.reason.noPlayback': 'ยังไม่ได้เชื่อมกับตัวเล่น',
  'rotation.reason.unavailable': 'ใช้ไม่ได้',

  // MVP-414, retitled by MVP-420. Not yet owner-reviewed.
  'history.open': 'ประวัติรอบนี้',
  'history.title': 'ร้องไปแล้วในรอบนี้',
  'history.loading': 'กำลังโหลดประวัติรอบนี้',
  'history.loadFailed': 'โหลดประวัติรอบนี้ไม่ได้',
  'history.emptyTitle': 'รอบนี้ยังไม่มีใครร้อง',
  // MVP-420: the span the list actually covers. Not yet owner-reviewed.
  'history.since': 'ตั้งแต่ {date}',
  'history.songCount': '{count} เพลง',
  'history.key': 'คีย์ {key}',
  'history.cut': 'ตัดกลางคัน',
  'history.requeue': 'เข้าคิวอีกครั้ง',
  // MVP-426. Not yet owner-reviewed.
  'history.songGone': 'เพลงถูกลบแล้ว',
  'history.requeueGone': 'เข้าคิวซ้ำไม่ได้',
  'history.requeued': 'เข้าคิว {song} ให้ {name} อีกครั้งแล้ว',
  'history.error.requeue': 'เข้าคิว {song} อีกครั้งไม่ได้',
  'history.exportCsv': 'ดาวน์โหลด CSV',
  // MVP-498. Not yet owner-reviewed.
  'history.tabs': 'ประวัติของห้องนี้',
  'history.tab.sung': 'ร้องไปแล้ว',
  'history.tab.requests': 'คำขอเพลง',
  'history.request.loading': 'กำลังโหลดคำขอเพลงของห้องนี้',
  'history.request.loadFailed': 'โหลดคำขอเพลงของห้องนี้ไม่ได้',
  'history.request.emptyTitle': 'รอบนี้ยังไม่มีใครขอเพลง',
  'history.request.by': 'ขอโดย {name}',
  'history.request.songGone': 'เพลงถูกลบแล้ว',
  'history.request.note': 'ข้อความ: {note}',
  'history.request.rejectReason': 'เหตุผล: {reason}',
  'history.request.outcome.pending': 'รอพิจารณา',
  'history.request.outcome.accepted': 'รับแล้ว',
  'history.request.outcome.rejected': 'ปฏิเสธแล้ว',
  'history.request.outcome.expired': 'หมดเวลา',

  // MVP-498 slice 2 — ย้ายห้อง. The two label strings are OWNER-APPROVED (2026-09-17) and are the
  // glossary's own words; do not paraphrase them. Everything else in this block is
  // "MVP-498. Not yet owner-reviewed."
  'ops.roomMove.action': 'ย้ายห้อง',
  'ops.roomMove.heading': 'ย้ายประวัติไปห้องอื่น',
  // MVP-498. Not yet owner-reviewed. Owner decision 2 made explicit BEFORE the operator commits:
  // `playback-state` belongs to the ROOM, and the room is not what moved — so the room being left
  // stops mid-song. That is the one consequence a move back does not undo, which is why it is in the
  // confirm body and not in a toast afterwards.
  'ops.roomMove.confirmBody':
    'คำขอเพลงและประวัติการร้องทั้งหมดของรอบนี้จะย้ายไปห้อง {room} ลูกค้าใช้รหัสเข้าร่วมเดิมได้เลย แต่เพลงที่กำลังเล่นอยู่ในห้องเดิมจะหยุดทันที',
  'ops.roomMove.targetLabel': 'ย้ายไปห้อง',
  // MVP-599. Not yet owner-reviewed. The picker's empty first option — the state the control is in
  // until someone chooses, and it has to say so rather than defaulting to a room nobody picked.
  'ops.roomMove.choose': 'เลือกห้อง…',
  'ops.roomMove.confirm': 'ย้ายเลย',
  'ops.roomMove.done': 'ย้ายไปห้อง {room} แล้ว',
  // MVP-606. Not yet owner-reviewed. Shown beside the picker when this branch has no other open
  // room — a one-room shop, not an error, so the sentence states the fact and does not apologise.
  // "สาขานี้" (this branch) is the word that carries the whole point: rooms in the customer's OTHER
  // branch exist and are deliberately not offered, because a night cannot change buildings.
  'ops.roomMove.noTargets': 'สาขานี้ไม่มีห้องอื่นที่ย้ายไปได้',
  'ops.roomMove.error.busy': 'ห้องนั้นมีรอบร้องอยู่แล้ว กรุณาจบรอบของห้องนั้นก่อนแล้วค่อยย้าย',
  'ops.roomMove.error.roomGone': 'ไม่พบห้องนั้นแล้ว',
  'ops.roomMove.error.failed': 'ย้ายห้องไม่สำเร็จ กรุณาลองอีกครั้ง',
  // MVP-647. Not yet owner-reviewed. ใช้คำเดิมของบล็อกนี้: รอบ = session, สาขา = venue
  // สองอันแรกแปลว่าคนคุมอีกเครื่องทำไปก่อนแล้ว จึงบอกให้ "ดูใหม่" ไม่ใช่ "ลองอีกครั้ง" — ลองไปก็โดนปฏิเสธด้วยเหตุผลเดิม
  // และ runMove ก็สั่งโหลดข้อมูลรอบใหม่ทั้งสองกรณี ประโยคจึงพูดได้ว่าหน้าจอรีเฟรชให้แล้วจริง
  'ops.roomMove.error.sessionMoved': 'มีคนอื่นย้ายรอบนี้ไปห้องอื่นไปเมื่อสักครู่ ระบบรีเฟรชหน้าจอให้แล้ว กรุณาดูว่าตอนนี้รอบอยู่ห้องไหนก่อนย้ายอีกครั้ง',
  'ops.roomMove.error.sessionEnded': 'รอบนี้จบไปแล้ว จึงไม่มีอะไรให้ย้าย ระบบรีเฟรชหน้าจอให้แล้ว',
  'ops.roomMove.error.venueMismatch': 'ห้องนั้นอยู่คนละสาขา และรอบร้องย้ายข้ามสาขาไม่ได้ กรุณาเลือกห้องในสาขานี้',

  // MVP-642 — เปลี่ยนรหัสเข้าร่วม. ทั้งบล็อกนี้ "MVP-642. Not yet owner-reviewed." — the glossary
  // word for the code itself ("รหัสเข้าร่วม") is taken from `ops.roomMove.confirmBody`, which IS
  // owner-approved and already uses it; the sentences around it are not reviewed.
  'ops.joinCode.heading': 'เปลี่ยนรหัสเข้าร่วม',
  'ops.joinCode.action': 'เปลี่ยนรหัสใหม่',
  'ops.joinCode.current': 'รหัสตอนนี้',
  // MVP-642. Not yet owner-reviewed. Says WHEN to use it, because the control is otherwise a button
  // with no occasion: a code that leaked is the only reason to press it.
  'ops.joinCode.note': 'ใช้เมื่อรหัสหลุดออกไป เช่น มีคนถ่ายรูป QR ไว้ หรือลืมบัตรรหัสไว้ที่โต๊ะ',
  // MVP-642. Not yet owner-reviewed. The consequence stated BEFORE the operator commits, and stated
  // as what happens to the guests rather than as "are you sure?": every phone in the room holding
  // the old code is cut off, the printed cards and the QR stop working, and there is no undo — the
  // old code is stored nowhere. The code being replaced is named so the KJ can check it against the
  // card in their hand before pressing.
  'ops.joinCode.confirmBody':
    'รหัส {code} จะใช้ไม่ได้ทันที ลูกค้าที่ยังไม่ได้เข้าร่วมจะต้องสแกน QR หรือกรอกรหัสใหม่ บัตรรหัสที่พิมพ์ไว้และ QR ที่ถ่ายรูปไว้จะใช้ไม่ได้อีก และย้อนกลับไม่ได้ ส่วนคนที่เข้าร่วมแล้วยังอยู่ในรอบตามเดิม',
  'ops.joinCode.confirm': 'เปลี่ยนเลย',
  'ops.joinCode.done': 'รหัสเข้าร่วมใหม่คือ {code}',
  // MVP-642. Not yet owner-reviewed. One sentence per refusal the route can answer — see
  // `features/ops/joinCodeRotate.ts` for which server condition each one is. None of these may be
  // folded into `failed`: that is the defect MVP-641/644 are open for.
  'ops.joinCode.error.sessionGone': 'รอบนี้จบไปแล้ว จึงไม่มีรหัสให้เปลี่ยน กรุณาเริ่มรอบใหม่',
  'ops.joinCode.error.forbidden': 'บัญชีนี้ไม่มีสิทธิ์เปลี่ยนรหัสเข้าร่วม กรุณาแจ้งเจ้าของร้าน',
  'ops.joinCode.error.signedOut': 'เซสชันเข้าสู่ระบบหมดอายุ กรุณาเข้าสู่ระบบใหม่แล้วลองอีกครั้ง',
  'ops.joinCode.error.failed': 'เปลี่ยนรหัสไม่สำเร็จ รหัสเดิมยังใช้ได้อยู่ กรุณาลองอีกครั้ง',
  // MVP-498. Not yet owner-reviewed. Owner decision 3: the guest is TOLD, in one line, rather than
  // silently reconnected. The reconnect happens either way — see RequestPage.tsx's `session.roomMoved`
  // branch — so the only choice was whether the phone says anything while it does.
  'guest.roomMoved.notice': 'ย้ายห้องแล้ว กำลังเชื่อมต่อใหม่ รหัสเดิมใช้ได้ตามปกติ',

  'requests.title': 'คำขอเพลง',
  'requests.pendingCount': 'รอ {count} รายการ',
  'requests.emptyTitle': 'ยังไม่มีคำขอเพลง',
  'requests.emptyDescription': 'ลูกค้าที่สแกน QR เข้าร่วมจะมาโผล่ที่นี่',
  'requests.accept': 'รับ',
  'requests.reject': 'ปฏิเสธ',
  // MVP-557. Not yet owner-reviewed. Spoken Thai a KJ would actually say across a counter, not a
  // clock reading: "รอ 6 นาที", never "6 น." or "6m". "ชม." is the ordinary written short form of
  // ชั่วโมง and is what an elderly reader expects on a sign.
  'requests.wait.justNow': 'เพิ่งส่งมา',
  'requests.wait.minutes': 'รอ {minutes} นาที',
  'requests.wait.hours': 'รอ {hours} ชม.',
  'requests.wait.hoursMinutes': 'รอ {hours} ชม. {minutes} นาที',
  // The marker on the row that has waited longest — the row the `a` key accepts.
  'requests.oldest': 'รอนานที่สุด',
  'requests.keyShift': 'คีย์ {value}',

  // DESIGN.md §6.4's connection language. Read by BOTH a bar customer on their phone and the KJ at
  // the desk, so this is the one place in Phase 3 where the plainer, customer Thai wins over
  // operator shorthand — "การเชื่อมต่อ" rather than a loan word.
  'common.connection.connected': 'เชื่อมต่อแล้ว',
  'common.connection.reconnecting': 'กำลังเชื่อมต่อข้อมูลสดใหม่…',
  'common.connection.reconnectingDetail': 'ยังสั่งงานได้ตามปกติ คิวและรอบร้องจะอัปเดตตามเมื่อเชื่อมต่อได้อีกครั้ง',
  'common.connection.reauth': 'กำลังเข้าสู่ระบบใหม่…',
  'common.connection.offline': 'ออฟไลน์',
  'common.connection.offlineDetail': 'สิ่งที่สั่งไว้อาจยังไม่ถูกบันทึกจนกว่าจะเชื่อมต่อได้',
  'common.connection.hostUnreachable': 'ติดต่อ {host} ไม่ได้',
  'common.connection.updatedAgo': 'อัปเดตเมื่อ {age} ที่แล้ว',
  'common.connection.srStatus': 'การเชื่อมต่อ: {status}',

  // MVP-483. Not yet owner-reviewed. Same register as the block above — this is read by a bar
  // customer on their phone and by the KJ at the desk, so plain customer Thai wins over operator
  // shorthand. "ข้อมูลที่เห็นอยู่อาจไม่ใช่ล่าสุด" is the sentence the whole task turns on, and it is
  // repeated verbatim in the guest copy on purpose: the two screens must not describe the same
  // outage differently.
  'common.outage.stagePill': 'กำลังเชื่อมต่อใหม่',
  'common.outage.stageVeil': 'กำลังเชื่อมต่อกับเซิร์ฟเวอร์อีกครั้ง อีกสักครู่จะกลับมาเหมือนเดิม',
  'common.outage.stale': 'ข้อมูลที่เห็นอยู่อาจไม่ใช่ล่าสุด',
  'common.outage.liveRegion': 'ส่วนนี้หยุดอัปเดตสดชั่วคราว แต่ยังรีเฟรชทุก 15 วินาที',
  'common.outage.guestTitle': 'ตอนนี้ติดต่อร้านไม่ได้',
  'common.outage.guestBody': 'โทรศัพท์ของคุณติดต่อเซิร์ฟเวอร์ไม่ได้ ข้อมูลที่เห็นอยู่อาจไม่ใช่ล่าสุด อยู่หน้านี้ไว้ เดี๋ยวระบบจะกลับมาเอง',
  'common.outage.guestRetry': 'ลองอีกครั้ง',
  'common.outage.guestRetrying': 'กำลังลอง…',
  'common.outage.guestRetryFailed': 'ยังติดต่อไม่ได้ รอสักครู่แล้วลองใหม่',

  // MVP-272 Phase 3c-1 (console shell). The rail is six icons with one word under each — the Thai
  // has to fit the same 56px strip, so these are the short spoken words a KJ would use, not the
  // literal renderings ("ห้องคุม", not "ห้องนักบิน").
  'nav.library': 'คลังเพลง',
  'nav.cockpit': 'ห้องคุม',
  'nav.settings': 'ตั้งค่า',
  'nav.ingest': 'นำเข้าเพลง',
  'nav.accounts': 'บัญชีผู้ใช้',
  'nav.activity': 'บันทึกกิจกรรม',
  // MVP-358
  'nav.withheld': 'เพลงที่ถูกเก็บ',

  'app.skipToContent': 'ข้ามไปยังเนื้อหา',
  'app.manual': 'คู่มือ',
'app.signOut': 'ออกจากระบบ',
  'app.playbackStartFailed': 'เริ่มเล่นเพลงไม่ได้',
  'app.viewChangedAnnouncement': 'ตอนนี้อยู่หน้า{view}',
  'app.miniTransport.regionLabel': 'ปุ่มควบคุมเพลงแบบย่อ',

  // "รอบ" is the word this app already uses for a night's session (see the rotation keys) — the
  // loan word "เซสชัน" would be the only English-shaped noun on an otherwise Thai wall.
  'ops.session.noneTitle': 'ยังไม่ได้เปิดรอบ',
  'ops.session.noneDescription': 'รอบร้อง รหัสเข้าร่วม และ QR ของคืนนี้อยู่บนรอบทั้งหมด เปิดรอบก่อนถึงจะเข้าห้องคุมได้',
  'ops.session.start': 'เปิดรอบคืนนี้',
  'ops.session.startFailed': 'เปิดรอบไม่ได้ — {reason}',
  'ops.session.startFailedFallback': 'คำสั่งไม่สำเร็จ',

  'ops.stageDisplay.title': 'เลือกจอที่จะใช้เป็นจอเวที',
  'ops.stageDisplay.hint': 'จอที่เลือกแต่ละจอจะได้หน้าต่างจอเวทีของตัวเอง เต็มจอนั้นพอดี และจำไว้ให้ครั้งต่อไปบนเครื่องนี้',
  'ops.stageDisplay.groupLabel': 'จอที่จะใช้เป็นจอเวที',
  'ops.stageDisplay.primaryBadge': 'จอหลัก',
  'ops.stageDisplay.rememberedBadge': 'จำไว้แล้ว',
  'ops.stageDisplay.confirm': 'เปิดจอเวที',
  'ops.stageDisplay.unnamedPrimary': 'จอหลัก',
  'ops.stageDisplay.unnamedSecondary': 'จอรอง',

  // MVP-272 Phase 3c-2 (the cockpit). The KJ reads these mid-shift with a room waiting, so every
  // failure sentence names the thing that did not happen first and the reason second.
  'common.requestFailed': 'คำสั่งไม่สำเร็จ',

  'ops.request.acceptFailed': 'รับคำขอนี้ไม่ได้ — {reason}',
  'ops.request.rejectFailed': 'ปฏิเสธคำขอนี้ไม่ได้ — {reason}',
  // MVP-329: the reject undo. The toast names the guest and the song deliberately — a KJ
  // moderating four requests in a row needs to know WHICH one they just turned down.
  'ops.request.rejected': 'ปฏิเสธเพลง "{title}" ของ {singer} แล้ว',
  // MVP-546. Not yet owner-reviewed. The accept receipt names the guest and the song for the same
  // reason the reject one does — the tray row is gone the moment the write lands.
  'ops.request.accepted': 'รับเพลง "{title}" ของ {singer} เข้าคิวแล้ว',
  'ops.request.thisGuest': 'แขกคนนี้',
  'ops.request.restoreFailed': 'ยกเลิกการปฏิเสธไม่ได้ — {reason}',
  'ops.request.restoreTooOld': 'คำขอหมดเวลาไปแล้ว เอากลับมาก็จะหมดอายุทันที',
  'ops.request.restoreNotRestorable': 'คำขอนี้ไม่ได้อยู่สถานะถูกปฏิเสธแล้ว อาจมีคนอื่นตัดสินไปก่อน',
  // MVP-556. Not yet owner-reviewed. The accept undo — `restoreTooOld` above is reused as-is,
  // because the server answers both undos with the same code for the same reason.
  'ops.request.unacceptFailed': 'ยกเลิกการรับคำขอไม่ได้ — {reason}',
  'ops.request.unacceptTooLate': 'เพลงนี้ขึ้นจอหรือร้องไปแล้ว จึงยังอยู่ในคิว',
  'ops.request.unacceptNotAccepted': 'คำขอนี้ไม่ได้อยู่สถานะรับแล้ว อาจมีคนอื่นเปลี่ยนไปก่อน',

  'ops.error.startNextFailed': 'เริ่มเพลง "{title}" ไม่ได้',
  'ops.error.theNextSong': 'เพลงถัดไป',
  'ops.error.closeOutFailed': 'ปิดเพลงที่เพิ่งจบไม่ได้',
  'ops.error.advanceFailed': 'ไปคิวถัดไปไม่ได้ — {reason}',
  'ops.error.reorderFailed': 'สลับคิวเพื่อเล่น "{title}" ไม่ได้ — {reason}',
  'ops.error.playNowFailed': 'เล่น "{title}" ตอนนี้ไม่ได้ — {reason}',
  'ops.error.thisSong': 'เพลงนี้',
  'ops.error.undoFailed': 'ย้อนกลับไม่ได้ — {reason}',
  'ops.error.cutUndoFailed': 'เอาเพลงนั้นกลับมาเล่นไม่ได้ — {reason}',
  // MVP-376: สแต็ก undo ถูกแก้หลังใบเสร็จขึ้น จึงไม่ย้อนอะไรเลย
  'ops.error.cutUndoForeign': 'ไม่ได้ย้อนกลับ — คิวถูกแก้หลังจากหยุดเพลงนั้นแล้ว ไม่มีอะไรเปลี่ยน',
  'ops.error.controlsLocked': 'ปุ่มควบคุมถูกล็อกอีก {seconds} วิ',
  'ops.error.rateLimitedRetry': 'สั่งถี่เกินไป ลองใหม่อีก {seconds} วิ',
  'ops.error.rateLimited': 'สั่งถี่เกินไป ช้าลงหน่อยแล้วลองใหม่',
  'ops.error.controlWriteFailed': 'บันทึกการเปลี่ยนไม่สำเร็จ ลองใหม่อีกครั้ง',

  // MVP-370: ใบเสร็จของการตัดเพลงที่กำลังร้องอยู่ พร้อมทางกลับ
  'ops.toast.cutWithSinger': 'หยุดเพลง “{title}” ที่ {singer} กำลังร้องอยู่',
  'ops.toast.cutTitleOnly': 'หยุดเพลง “{title}” แล้ว',
  'ops.toast.cutUnnamed': 'หยุดเพลงที่กำลังเล่นอยู่แล้ว',
  'ops.toast.cutUndoDetail': 'กดย้อนกลับเพื่อเล่นต่อ — เพลงจะเริ่มใหม่จากต้นเพลง',
  'ops.toast.cutUndoAction': 'ย้อนกลับ',
  'ops.toast.undone': 'ย้อนกลับแล้ว',
  'ops.toast.stageMovedTitle': 'ย้ายจอเวทีมาที่จอหลักแล้ว',
  'ops.toast.stageMovedDetail': 'จอเดิมถูกถอดออก',

  'ops.video.wide': 'ขยายภาพกว้าง',
  'ops.video.exitWide': 'เลิกขยายภาพกว้าง',
  'ops.video.fullscreen': 'เต็มจอ',
  'ops.video.exitFullscreen': 'ออกจากเต็มจอ',
  'ops.video.resume': 'คลิกเพื่อเล่นต่อ',
  // MVP-548. Not yet owner-reviewed.
  'ops.video.hidePreview': 'ซ่อนภาพตัวอย่าง',
  // MVP-548. Not yet owner-reviewed.
  'ops.video.showPreview': 'แสดงภาพตัวอย่าง',

  'ops.nothingPlayingTitle': 'ยังไม่มีเพลงเล่นอยู่',
  'ops.nothingPlayingDescription': 'เลือกเพลงจากคลังเข้าคิว แล้วกดเริ่มเพลงถัดไป',

  // MVP-676. The cockpit's right side panel — the owner's own name for the column.
  'ops.rightSidePanel.hide': 'ซ่อนแผงด้านขวา',
  'ops.rightSidePanel.show': 'แสดงแผงด้านขวา',
  'ops.rightSidePanel.joinCode': 'โค้ดเข้าร่วม',
  'ops.thisSession': 'รอบนี้',
  'ops.stage.open': 'เปิดจอเวที',
  'ops.stage.focus': 'ไปที่จอเวที',

  // MVP-500. Not yet owner-reviewed. "หน้าร้าน" is the owner's own word for the front-of-house
  // machine; the middle sentence names what is locked, because in this mode the strip is the only
  // thing left on screen to explain a key that did nothing.
  'ops.storefront.enter': 'เข้าสู่โหมดหน้าร้าน',
  'ops.storefront.active': 'โหมดหน้าร้าน — ปุ่มที่ลบข้อมูลหรือแก้ไขการตั้งค่าถูกล็อกไว้',
  'ops.storefront.exit': 'ออกจากโหมดหน้าร้าน',
  // MVP-678. The gate on the way out. "รหัสผ่านร้าน" is the same name Tonight settings gives the
  // field that sets it — one secret, one word for it, or the KJ is hunting for a second password
  // that does not exist.
  'ops.storefront.passcodeTitle': 'ออกจากโหมดหน้าร้าน',
  'ops.storefront.passcodeLabel': 'รหัสผ่านร้าน',
  'ops.storefront.passcodeWrong': 'รหัสไม่ถูก ลองใหม่',

  // MVP-513. Not yet owner-reviewed. แถบนับถอยหลังวันหมดอายุใบอนุญาตที่ร้านเห็นทุกวัน ตั้งแต่ 7 วันก่อน
  // จนถึงช่วงผ่อนผันอีก 7 วันหลังวันหมดอายุ. เขียนด้วยคำที่ผู้ใช้หน้าร้านอ่านแล้วรู้ทันทีว่าต้อง “ติดต่อผู้ดูแลระบบ”
  // ไม่ใช่กดปุ่มอะไรเอง — เพราะร้านต่ออายุเองไม่ได้. 'ช่วงผ่อนผัน' คือคำที่ใช้คู่กับ grace window ฝั่งเซิร์ฟเวอร์.
  'ops.licence.ending': 'ใบอนุญาตจะหมดอายุในอีก {days} วัน',
  'ops.licence.endingToday': 'ใบอนุญาตหมดอายุวันนี้ — ยังใช้งานได้ตามปกติ',
  'ops.licence.grace': 'ใบอนุญาตหมดอายุมาแล้ว {days} วัน — อยู่ในช่วงผ่อนผัน ใช้งานได้อีก {graceDays} วัน',
  'ops.licence.graceLastDay': 'ใบอนุญาตหมดอายุมาแล้ว {days} วัน — วันนี้เป็นวันสุดท้ายที่ใช้งานได้',
  'ops.licence.contact': 'ติดต่อผู้ดูแลระบบเพื่อต่ออายุ',
  'ops.licence.dismissToday': 'ซ่อนไว้จนถึงพรุ่งนี้',

  // "เริ่มใหม่" not "ย้อนกลับ" — this button restarts the current song, it does not walk the
  // rotation backwards, and the Thai has to say which.
  'player.transport.restart': 'เริ่มเพลงใหม่',
  'player.transport.play': 'เล่น',
  'player.transport.pause': 'พักเพลง',
  // MVP-427. Not yet owner-reviewed.
  'player.transport.playPreview': 'เล่น (จอตัวอย่าง)',
  'player.transport.pausePreview': 'พักเพลง (จอตัวอย่าง)',
  'player.transport.stop': 'หยุด',
  'player.transport.next': 'ถัดไป',
  'player.transport.nextNothingQueued': 'ถัดไป: ไม่มีเพลงในคิว',

  'player.setup.lead': 'ระบบเล่นสื่อตั้งค่าไม่ถูกต้อง',
  'player.setup.contactAdmin': 'ติดต่อผู้ดูแลระบบเพื่อแก้ไข',
  'player.setup.details': 'รายละเอียด',
  'player.setup.retry': 'ตรวจใหม่',

  'player.setup.warning.noRangeSupport.title': 'การเลื่อนตำแหน่งเพลงบนเซิร์ฟเวอร์สื่ออาจช้า',
  'player.setup.warning.noRangeSupport.detail': 'เซิร์ฟเวอร์สื่อส่งไฟล์ทั้งไฟล์แทนที่จะส่งเฉพาะช่วงที่ขอ การเลื่อนตำแหน่งเพลงจึงอาจโหลดใหม่แทนที่จะกระโดดไปทันที',
  'player.setup.warning.rangeNotConfirmed.title': 'เลื่อนตำแหน่งเพลงได้ตามปกติ แต่ยังตรวจสอบเซิร์ฟเวอร์สื่อไม่ครบถ้วน',
  'player.setup.warning.rangeNotConfirmed.detail': 'เซิร์ฟเวอร์สื่อตอบกลับมาแล้ว แต่หน้านี้ยังยืนยันคำตอบไม่ครบถ้วน การเลื่อนตำแหน่งเพลงน่าจะยังใช้งานได้ตามปกติ',
  'player.setup.warning.webAudioUnconfirmed.title': 'ยังไม่ได้ตรวจสอบเสียง',
  'player.setup.warning.webAudioUnconfirmed.detail': 'การเล่นถูกบล็อกก่อนที่คุณจะกด จึงยังตรวจสอบเสียงไม่ได้ ระบบจะตรวจใหม่อีกครั้งในครั้งถัดไป',
  'player.setup.warning.webAudioSilent.title': 'ไฟล์นี้อาจไม่มีเสียง',
  'player.setup.warning.webAudioSilent.detail': 'ไม่มีเสียงมาถึงจุดตรวจสอบภายในหนึ่งวินาที ไฟล์นี้อาจไม่มีแทร็กเสียง หรือมีช่วงเงียบตอนต้นเพลง',

  'player.link.measuring': 'กำลังวัด…',
  'player.link.comfortable': 'สบาย',
  'player.link.conservative': 'ประหยัด',

  'player.output.trigger': 'ลำโพง',
  'player.output.ariaLabel': 'อุปกรณ์เสียงออก: {device}',
  'player.output.unnamedDevice': 'ช่องเสียง {index}',
  'player.output.popoverTitle': 'อุปกรณ์เสียงออก',
  'player.output.systemDefault': 'ค่าเริ่มต้นของเครื่อง',
  'player.output.unsupported':
    'เบราว์เซอร์นี้เลือกอุปกรณ์เสียงออกเองไม่ได้ (ต้องใช้ Chrome 110 ขึ้นไป) เสียงจะออกทางอุปกรณ์ค่าเริ่มต้นของเครื่อง',
  'player.output.selectLabel': 'อุปกรณ์เสียงออก',
  'player.output.test': 'ทดสอบ',
  'player.output.testFailed': 'เล่นเสียงทดสอบไม่ได้',
  'player.output.errorUnsupported': 'เบราว์เซอร์นี้เลือกอุปกรณ์เสียงออกไม่ได้ เสียงจะออกทางอุปกรณ์หลักของเครื่อง',
  'player.output.errorFailed': 'สลับอุปกรณ์เสียงออกไม่ได้',

  'player.break.popoverTitle': 'เพลงคั่น',
  'player.break.start': 'เริ่มเพลงคั่น',
  'player.break.stop': 'หยุดเพลงคั่น',
  'player.break.loadFailed': 'โหลดรายการเพลงคั่นไม่ได้',
  'player.break.empty': 'ยังไม่มีเพลงไหนถูกทำเครื่องหมายเป็นเพลงคั่น ไปที่คลังเพลง เลือกเพลง แล้วกด “ทำเป็นเพลงคั่น”',
  'player.break.alsoOnList': 'มีในรายการด้วย',
  'player.break.upNextShuffled': 'คิวถัดไป (สุ่ม)',
  'player.break.duck': 'หรี่เสียงเพื่อประกาศ',
  'player.break.startFailed': 'เริ่มเพลงคั่นไม่ได้',

  // ชื่อปุ่มบนคีย์บอร์ด (Space, Ctrl, Enter, Esc, Page Up, A–Z) ไม่แปล — เป็นตัวอักษรที่พิมพ์อยู่บนแป้นจริง
  'ops.shortcuts.title': 'ปุ่มลัดคีย์บอร์ด',
  'ops.shortcuts.sectionCockpit': 'ห้องคุม',
  'ops.shortcuts.sectionLibrary': 'คลังเพลง',
  // MVP-339
  'ops.shortcuts.filterLabel': 'กรองปุ่มลัด',
  'ops.shortcuts.filterPlaceholder': 'พิมพ์ชื่อคำสั่งหรือชื่อปุ่ม',
  'ops.shortcuts.filterClear': 'ล้างตัวกรอง',
  'ops.shortcuts.noMatch': 'ไม่มีปุ่มลัดที่ตรงกับที่พิมพ์',
  'ops.shortcuts.conflict': 'ปุ่มนี้ถูกใช้กับ “{action}” อยู่แล้ว เปลี่ยนปุ่มของคำสั่งนั้นก่อน หรือเลือกปุ่มอื่น',
  // MVP-500 Commit C. Not yet owner-reviewed. ชื่อแผ่นปุ่มลัดตอนเปิดโหมดหน้าร้าน — ตั้งใจไม่เรียกว่า
  // “เลย์เอาต์ eXtreme” เพราะย้ายแค่ 3 ปุ่ม ("eXtreme" คงเป็นอักษรละติน — เป็นชื่อโปรแกรมที่ KJ เห็นบนหน้าจอโปรแกรมนั้น)
  'ops.shortcuts.titleStorefront': 'ปุ่มลัดคีย์บอร์ด — โหมดหน้าร้าน',

  'ops.hotkey.playPause': 'เล่น / หยุดชั่วคราว',
  'ops.hotkey.next': 'คนถัดไป / เพลงถัดไป',
  'ops.hotkey.previous': 'เริ่มเพลงนี้ใหม่',
  'ops.hotkey.stop': 'หยุด',
  'ops.hotkey.keyUp': 'คีย์สูงขึ้น (+1 เสียง)',
  'ops.hotkey.keyDown': 'คีย์ต่ำลง (−1 เสียง)',
  'ops.hotkey.keyReset': 'คืนคีย์เป็น 0',
  // MVP-474. Not yet owner-reviewed.
  'ops.hotkey.tempoUp': 'จังหวะเร็วขึ้น (+1%)',
  'ops.hotkey.tempoDown': 'จังหวะช้าลง (−1%)',
  'ops.hotkey.offsetDown': 'เลื่อนซิงค์ −5 มิลลิวินาที',
  'ops.hotkey.offsetUp': 'เลื่อนซิงค์ +5 มิลลิวินาที',
  'ops.hotkey.mute': 'ปิดเสียง',
  'ops.hotkey.breakMusic': 'เปิด/ปิดเพลงคั่น',
  'ops.hotkey.fullscreen': 'ขยายจอเวทีเต็มจอ',
  'ops.hotkey.toggleLyrics': 'เปิด/ปิดเนื้อเพลง',
  'ops.hotkey.search': 'ไปที่ช่องค้นหา',
  'ops.hotkey.shortcutSheet': 'ดูรายการปุ่มลัดนี้',
  'ops.hotkey.undo': 'ย้อนกลับ',
  // MVP-547. Not yet owner-reviewed. บอกว่าเป็น “รายการไหน” ไม่ใช่แค่ “รับ” — ถ้ามีคนรออยู่สี่คิว
  // คำว่ารับเฉย ๆ ทำให้เดาไม่ออกว่าปุ่มนี้รับของใคร
  'ops.hotkey.acceptTopRequest': 'รับคำขอเพลงที่รอนานที่สุด',
  'ops.hotkey.exitStorefront': 'ออกจากโหมดหน้าร้าน',

  // MVP-500 Commit C. Not yet owner-reviewed. ป้ายกำกับที่ใช้แทนของเดิม “เฉพาะตอนเปิดโหมดหน้าร้าน” — สามปุ่มที่ KJ
  // ซึ่งชินมือกับ eXtreme จะกดแล้วได้ผลไม่เหมือนที่คาด การบอกไว้บนแผ่นปุ่มลัดคือทางป้องกันเดียวที่มี
  'ops.hotkey.storefront.playPause': 'เล่น / หยุดชั่วคราว (eXtreme ใช้ปุ่มนี้แสดงชื่อเพลง)',
  'ops.hotkey.storefront.tempoUp': 'จังหวะเร็วขึ้น (+1%) (eXtreme ใช้ปุ่มนี้เลื่อนดูคิว)',
  'ops.hotkey.storefront.tempoDown': 'จังหวะช้าลง (−1%) (eXtreme ใช้ปุ่มนี้เลื่อนดูคิว)',

  // MVP-263: “ที่เลือกไว้” คือเพลงที่ติ๊กถูกไว้หลายเพลง ส่วน “ที่ชี้อยู่” คือเพลงเดียวที่กรอบโฟกัสอยู่ — คนละความหมาย ห้ามใช้คำเดียวกัน
  'library.shortcut.focusSearch': 'ไปที่ช่องค้นหา',
  'library.shortcut.move': 'ย้ายกรอบที่ชี้อยู่',
  'library.shortcut.first': 'ไปเพลงแรก',
  'library.shortcut.last': 'ไปเพลงสุดท้าย',
  'library.shortcut.pageUp': 'เลื่อนขึ้นหนึ่งหน้า',
  'library.shortcut.pageDown': 'เลื่อนลงหนึ่งหน้า',
  'library.shortcut.play': 'เล่นเพลงที่ชี้อยู่ทันที',
  'library.shortcut.queue': 'เพิ่มเพลงที่เลือกไว้เข้าคิว',
  'library.shortcut.queueNext': 'เพิ่มเพลงที่เลือกไว้เข้าคิว ให้เล่นเป็นเพลงถัดไป',
  'library.shortcut.escape': 'ปิดแผงที่เปิดอยู่ หรือล้างเพลงที่เลือกไว้',
  'library.shortcut.sheet': 'ดูรายการปุ่มลัดนี้',
  'library.shortcut.typeAhead': 'พิมพ์ตัวอักษรเพื่อกระโดดไปชื่อเพลงที่ขึ้นต้นด้วยตัวนั้น',

  // ชื่อรูปแบบไฟล์ (LRC, ASS, WebVTT, CD+G) และชื่อ encoding (UTF-8, Shift-JIS) ไม่แปล — เป็นชื่อมาตรฐานที่ต้องตรงกับตัวไฟล์
  // ภาษาไทยไม่มีรูปพหูพจน์ สองคีย์ที่ลงท้าย One/Many จึงแปลเหมือนกัน
  'lyrics.warningCountOne': 'คำเตือน 1 รายการ',
  'lyrics.warningCountMany': 'คำเตือน {count} รายการ',

  'lyrics.settings': 'ตั้งค่าเนื้อเพลง',
  'lyrics.fixThisFile': 'แก้ไฟล์นี้',
  'lyrics.addLyrics': 'เพิ่มเนื้อเพลง',
  'lyrics.assFallback': 'ใช้ตัวแสดงซับขั้นสูงไม่ได้ ({reason}) จะแสดงเนื้อเพลงแบบไล่สีธรรมดาแทน',
  'lyrics.assUnsupported.worker': 'เบราว์เซอร์นี้ไม่รองรับ Web Worker',
  'lyrics.assUnsupported.webAssembly': 'เบราว์เซอร์นี้ไม่รองรับ WebAssembly',
  'lyrics.assUnsupported.offscreenCanvas': 'เบราว์เซอร์นี้ไม่รองรับ OffscreenCanvas',
  'lyrics.assUnsupported.loadFailed': 'โหลดตัวแสดงซับไม่สำเร็จ',

  'lyrics.cdg.error.http': 'โหลดไฟล์ CD+G ไม่สำเร็จ (HTTP {status})',
  'lyrics.cdg.error.unreachable': 'ติดต่อเซิร์ฟเวอร์เพื่อโหลดไฟล์ CD+G ไม่ได้',
  'lyrics.cdg.error.empty': 'ไฟล์ CD+G นี้ว่างเปล่า',
  'lyrics.cdg.error.generic': 'โหลดไฟล์ CD+G ไม่สำเร็จ',
  'lyrics.loadFailed': 'โหลดเนื้อเพลงไม่ได้: {reason}',
  'lyrics.loadError.missingFile': 'แทร็กนี้ไม่มีไฟล์เนื้อเพลงแนบอยู่',
  'lyrics.loadError.http': 'CDN ตอบกลับ {status}',
  'lyrics.loadError.emptyFile': 'ไฟล์ว่างเปล่า',

  'lyrics.source.label': 'แหล่งเนื้อเพลง',
  'lyrics.source.edit': 'แก้ไข',

  'lyrics.offset.label': 'เลื่อนซิงค์เนื้อเพลง',
  'lyrics.offset.saveFailed': 'บันทึกค่าเลื่อนซิงค์ไม่ได้ จะใช้ได้เฉพาะรอบนี้',

  'lyrics.encoding.label': 'รหัสตัวอักษรของไฟล์เนื้อเพลง',
  'lyrics.encoding.japanese': 'ญี่ปุ่น',
  'lyrics.encoding.chinese': 'จีน',
  'lyrics.encoding.latin': 'ละติน',
  'lyrics.encoding.thai': 'ไทย',

  'lyrics.tap.back': 'ถอยหลัง 2 วินาที',
  'lyrics.tap.forward': 'เดินหน้า 2 วินาที',
  'lyrics.tap.tap': 'เคาะ',

  'lyrics.editor.title': 'แก้เนื้อเพลง — {title}',
  'lyrics.editor.assShiftHint': 'เลื่อนทั้งไฟล์ — รูปแบบและตำแหน่งตัวอักษรเดิมทุกอย่างไม่เปลี่ยน',
  'lyrics.editor.wholeFileShift': 'เลื่อนทั้งไฟล์',
  'lyrics.editor.applyAndSave': 'ใช้แล้วบันทึก',
  'lyrics.editor.textLabel': 'เนื้อเพลง — บรรทัดละหนึ่งท่อน',
  'lyrics.editor.textPlaceholder': 'ท่อนแรกของเพลง\nท่อนที่สอง\n…',
  'lyrics.editor.startTiming': 'เริ่มจับเวลา',
  'lyrics.editor.import': 'นำเข้าไฟล์ (.lrc, .ass, .ssa, .srt, .vtt)',
  'lyrics.editor.needOneLine': 'พิมพ์อย่างน้อยหนึ่งบรรทัดก่อน บรรทัดละหนึ่งท่อน',
  'lyrics.editor.importUnsupported': 'นำเข้า “{file}” ไม่ได้ — รองรับเฉพาะ .lrc, .ass, .ssa, .srt และ .vtt',
  'lyrics.editor.importNoLines': 'อ่าน “{file}” ได้ แต่ไม่มีบรรทัดที่จับเวลาไว้ ลองดูเนื้อไฟล์อีกที',
  'lyrics.editor.importUnreadable': 'อ่านไฟล์ “{file}” ไม่ได้',
  'lyrics.editor.saveFailed': 'บันทึกเนื้อเพลงไม่ได้ ดูการเชื่อมต่อแล้วลองใหม่',
  'lyrics.editor.shiftSaveFailed': 'บันทึกไฟล์ที่เลื่อนแล้วไม่ได้',
  'lyrics.editor.warningsOne': 'มีคำเตือน 1 รายการจากไฟล์ต้นทาง เวลาที่จับไว้ด้านล่างยังใช้ได้',
  'lyrics.editor.warningsMany': 'มีคำเตือน {count} รายการจากไฟล์ต้นทาง เวลาที่จับไว้ด้านล่างยังใช้ได้',
  'lyrics.editor.statusWord': 'คำที่ {index} จาก {total} — “{word}”',
  'lyrics.editor.statusAllStamped': 'จับเวลาครบทุกบรรทัดแล้ว ปรับตรงที่เพี้ยนแล้วบันทึกได้เลย',
  'lyrics.editor.statusLine': 'บรรทัดที่ {index} จาก {total}',
  'lyrics.editor.jumpCursor': 'ย้ายจุดเคาะมาที่บรรทัดนี้',
  'lyrics.editor.nudgeLineEarlier': 'เลื่อนบรรทัดนี้เร็วขึ้น {step} มิลลิวินาที',
  'lyrics.editor.nudgeLineLater': 'เลื่อนบรรทัดนี้ช้าลง {step} มิลลิวินาที',
  'lyrics.editor.split': 'แยกบรรทัด',
  'lyrics.editor.merge': 'รวมกับบรรทัดล่าง ↓',
  'lyrics.editor.wordByWord': 'ทีละคำ',
  'lyrics.editor.shiftWholeFile': 'เลื่อนทั้งไฟล์',
  'lyrics.editor.shiftAllEarlier': 'เลื่อนทุกบรรทัดเร็วขึ้น {step} มิลลิวินาที',
  'lyrics.editor.shiftAllLater': 'เลื่อนทุกบรรทัดช้าลง {step} มิลลิวินาที',
  'lyrics.editor.languageLabel': 'ภาษา (ไม่บังคับ)',
  'lyrics.editor.languagePlaceholder': 'en, th, ja…',
  'lyrics.editor.saveFormat': 'บันทึกเป็นรูปแบบ',
  'lyrics.editor.formatEnhanced': 'Enhanced LRC (ระดับคำ)',
  'lyrics.editor.formatAss': 'ASS (มีสไตล์คาราโอเกะ)',
  'lyrics.editor.save': 'บันทึกเนื้อเพลง',

  'library.kind.video': 'วิดีโอ',
  'library.kind.karaoke': 'คาราโอเกะ',
  'library.kind.cdg': 'CD+G',
  'library.kind.audio': 'เสียง',

  'library.song.label': '{title} โดย {artist}',
  'library.song.select': 'เลือก {title}',
  'library.song.catalogueId': 'รหัสเพลง',
  'library.song.breakBadge': 'เพลงคั่น',
  'library.song.breakTitle': 'ใช้เล่นเป็นเพลงคั่นระหว่างคน',
  'library.song.lyricsAvailable': 'มีเนื้อเพลง',
  // MVP-656. Not yet owner-reviewed. ใช้คำเดิมของแอป: "เสียงร้องไกด์" = guide vocal (ดู player.vocal.guideVocalOn)
  'library.song.vocalOff': 'ปิดเสียงร้องไกด์ได้',

  'library.download.action': 'ดาวน์โหลด',
  'library.download.busy': 'กำลังดาวน์โหลด…',
  'library.download.label': 'ดาวน์โหลด {title}',

  // MVP-459. Not yet owner-reviewed.
  'library.audition.action': 'ฟังเทส',
  'library.audition.label': 'ฟังเทส {title}',
  'library.audition.stop': 'หยุดฟัง',
  'library.audition.stopLabel': 'หยุดฟัง {title}',

  // MVP-460. Not yet owner-reviewed.
  'library.audition.channelLeft': 'ฟังเฉพาะช่องซ้าย',
  'library.audition.channelRight': 'ฟังเฉพาะช่องขวา',
  'library.audition.channelBoth': 'กลับไปฟังสองช่อง',

  // ไทยไม่มีพหูพจน์ คีย์ One/Many จึงแปลเหมือนกัน
  'library.results.searchOne': 'เจอ {count} เพลง สำหรับ “{query}”',
  'library.results.searchMany': 'เจอ {count} เพลง สำหรับ “{query}”',
  'library.results.filtered': '{visible} เพลง จากทั้งหมด {total} เพลง',
  'library.results.totalOne': '{count} เพลง',
  'library.results.totalMany': '{count} เพลง',
  'library.selection.hiddenOne': 'เพลงที่เลือกไว้ {count} เพลงไม่อยู่ในรายการที่เห็นแล้ว',
  'library.selection.hiddenMany': 'เพลงที่เลือกไว้ {count} เพลงไม่อยู่ในรายการที่เห็นแล้ว',
  'library.selection.count': 'เลือกไว้ {count} เพลง',
  'library.selection.clear': 'ล้าง',

  'library.facet.kind': 'ประเภท',
  'library.facet.genre': 'แนวเพลง',
  'library.facet.language': 'ภาษา',
  'library.facet.decade': 'ยุค',
  'library.facet.lyrics': 'เนื้อเพลง',
  'library.facet.duration': 'ความยาว',
  'library.facet.resolution': 'ความคมชัด',
  'library.facet.played': 'ประวัติการเล่น',
  'library.facet.breakMusic': 'เพลงคั่น',
  // MVP-658. Not yet owner-reviewed. ค่าของ facet นี้ใช้คำเดียวกับตัวแก้ในหน้า cockpit (player.vocal.tag*)
  'library.facet.guideVocal': 'เสียงร้องไกด์',

  'library.facet.duration.under2': 'สั้นกว่า 2 นาที',
  'library.facet.duration.2to4': '2–4 นาที',
  'library.facet.duration.4to6': '4–6 นาที',
  'library.facet.duration.over6': 'ยาวกว่า 6 นาที',
  'library.facet.duration.unknown': 'ไม่ทราบความยาว',
  'library.facet.resolution.unknown': 'ไม่ทราบความคมชัด',
  'library.facet.played.played': 'เคยเล่นแล้ว',
  'library.facet.played.never': 'ยังไม่เคยเล่น',
  'library.facet.breakMusic.break': 'เพลงคั่นเท่านั้น',
  'library.facet.breakMusic.regular': 'เพลงปกติเท่านั้น',
  'library.facet.lyrics.wordLevel': 'เนื้อเพลงแบบไล่ทีละคำ',
  'library.facet.lyrics.line': 'เนื้อเพลงแบบทีละบรรทัด',
  'library.facet.lyrics.none': 'ไม่มีเนื้อเพลง',
  // MVP-311: หกแนวเพลงที่กำหนดไว้ที่ศิลปิน แล้วส่งต่อไปยังทุกเพลงของศิลปินคนนั้น
  'library.facet.genre.lukThung': 'ลูกทุ่ง',
  'library.facet.genre.morLam': 'หมอลำ',
  'library.facet.genre.string': 'สตริง',
  'library.facet.genre.sakon': 'สากล',
  'library.facet.genre.chinese': 'จีน',
  'library.facet.genre.phueaChiwit': 'เพื่อชีวิต',
  'library.facet.genre.southern': 'เพลงใต้',
  'library.facet.genre.unknown': 'ไม่ระบุ',
  'library.facet.unspecified': 'ไม่ได้ระบุ',
  'library.facet.decade.unknown': 'ไม่ทราบปี',

  'library.filters.title': 'ตัวกรอง',
  'library.filters.clearAll': 'ล้างทั้งหมด',
  'library.filters.innerSearchLabel': 'ค้นหาในกลุ่ม {group}',
  'library.filters.innerSearchPlaceholder': 'ค้นหา…',
  'library.filters.selectShown': 'เลือกทั้งหมดที่แสดงอยู่',
  'library.filters.clearShown': 'ล้างทั้งหมดที่แสดงอยู่',
  'library.filters.noMatches': 'ไม่พบ',

  'library.activeFilters.label': 'ตัวกรองที่ใช้อยู่',
  'library.activeFilters.remove': 'เอาตัวกรอง {group}: {value} ออก',

  'library.sort.label': 'เรียงเพลงตาม',
  'library.sort.relevanceTitle': 'ตอนค้นหา จะเรียงตามความตรงที่สุดก่อน',
  'library.sort.relevanceReason': 'เรียงตามความตรงที่สุด',
  'library.sort.codeDesc': 'รหัสเพลง (มาก–น้อย)',
  'library.sort.code': 'รหัสเพลง (น้อย–มาก)',
  'library.sort.title': 'ชื่อเพลง (ก–ฮ)',
  'library.sort.artist': 'ศิลปิน (ก–ฮ)',
  'library.sort.added': 'เพิ่มล่าสุด',
  'library.sort.played': 'เล่นบ่อยที่สุด',
  'library.sort.duration': 'ความยาว',

  'library.density.label': 'รูปแบบการแสดงคลังเพลง',
  'library.density.grid': 'แบบตาราง',
  'library.density.list': 'แบบรายการ',
  'library.density.compact': 'แบบรายการแบบแน่น',

  // MVP-432. Not yet owner-reviewed.
  'library.zoom.label': 'ขนาดการ์ด',
  'library.zoom.out': 'การ์ดเล็กลง',
  'library.zoom.in': 'การ์ดใหญ่ขึ้น',
  'library.zoom.sm': 'เล็ก',
  'library.zoom.md': 'กลาง',
  'library.zoom.lg': 'ใหญ่',
  'library.zoom.xl': 'ใหญ่สุด',
  'library.zoom.reset': 'รีเซ็ต',
  // MVP-448. Not yet owner-reviewed.
  'library.zoom.atSmallest': 'เล็กที่สุดแล้ว',
  'library.zoom.atLargest': 'ใหญ่ที่สุดเท่าที่หน้าจอนี้รับได้ — ขยายหน้าต่างหรือปิดแผงข้างเพื่อให้ใหญ่กว่านี้',
  'library.zoom.atDefault': 'ขนาดเริ่มต้นอยู่แล้ว',

  // MVP-442. Not yet owner-reviewed.
  'library.backToTop': 'กลับไปเพลงแรก',

  'common.serverUnreachable': 'ติดต่อเซิร์ฟเวอร์ไม่ได้',

  'library.search.label': 'ค้นหาในคลังเพลง',
  'library.search.placeholder': 'ค้นหาชื่อเพลง ชื่อศิลปิน…',
  'library.filters.show': 'แสดงตัวกรอง',
  'library.filters.hide': 'ซ่อนตัวกรอง',
  // MVP-312: ตัดคำว่า "ปี" กับ "ภาษา" ออกจากทั้งสองข้อความ — สองอย่างนี้ไม่ใช่ตัวกรองที่ "รอข้อมูล"
  // เหมือนแนวเพลงกับเนื้อร้องอีกต่อไป แต่เป็นข้อมูลที่ไฟล์ต้นทางไม่มีทางมีเลย (ไฟล์เพลงถูก ffmpeg
  // remux จนข้อมูลเดิมหายหมดตั้งแต่ต้นทาง)
  'library.filters.none': 'ยังไม่มีตัวกรอง — คลังนี้ยังไม่มีข้อมูลแนวเพลงหรือเนื้อร้อง',
  'library.filters.moreWhenData': 'จะมีตัวกรองเพิ่มอีก {count} แบบ เมื่อเพลงมีข้อมูลแนวเพลงหรือเนื้อร้อง',
  // MVP-415. Not yet owner-reviewed.
  'library.inspector.title': 'รายละเอียดเพลง',
  'library.inspector.show': 'ดูรายละเอียดเพลง',
  'library.inspector.hide': 'ซ่อนรายละเอียดเพลง',
  'library.inspector.emptyTitle': 'เลือกเพลงเพื่อดูรายละเอียด',
  'library.inspector.featuring': 'ร่วมกับ {name}',
  'library.inspector.hasLyrics': 'มีเนื้อร้อง',
  'library.inspector.breakMusic': 'เพลงคั่น',
  'library.inspector.status': 'สถานะ: {status}',
  'library.inspector.code': 'รหัสเพลง',
  'library.inspector.duration': 'ความยาว',
  'library.inspector.language': 'ภาษา',
  'library.inspector.genre': 'แนวเพลง',
  'library.inspector.year': 'ปี',
  'library.inspector.resolution': 'วิดีโอ',
  'library.inspector.playCount': 'เล่นไปแล้ว',
  'library.inspector.lastPlayed': 'เล่นล่าสุด',
  'library.inspector.never': 'ยังไม่เคย',
  'library.inspector.added': 'เพิ่มเมื่อ',
  'library.export.action': 'ส่งออก XLSX',
  'library.export.busy': 'กำลังส่งออก…',
  'library.export.title': 'ดาวน์โหลดเพลงทั้งหมดที่ตรงกับคำค้นและตัวกรองตอนนี้ เป็นไฟล์ .xlsx',
  'library.export.readyTitle': 'ส่งออกเสร็จแล้ว',
  'library.export.readyDetail': 'เขียน {count} เพลงลงไฟล์ .xlsx แล้ว',
  'library.export.failedTitle': 'ส่งออกไม่สำเร็จ',

  'library.action.selectFirst': 'เลือกเพลงก่อน',
  'library.action.playNow': 'เล่นเลย (ไม่ระบุคนร้อง)',
  'library.action.playNowTitle': 'เล่น “{title}” เลย โดยไม่เพิ่มเข้าคิว',
  'library.action.playNowOneOnly': 'เล่นเลยได้ทีละเพลง แต่ตอนนี้เลือกไว้ {count} เพลง ล้างที่เลือกไว้ หรือใช้ “เล่นเพลงถัดไป”',

  'library.action.queue': 'เพิ่มเข้าคิว',
  'library.action.queueCount': 'เพิ่ม {count} เพลงเข้าคิว',
  'library.action.queueOneTitle': 'เพิ่ม “{title}” เข้าคิว',
  'library.action.queueManyTitle': 'เพิ่มเพลงที่เลือกไว้ทั้ง {count} เพลงเข้าคิว ให้คนร้องคนเดียวกัน',
  'library.action.next': 'เล่นเพลงถัดไป',
  'library.action.nextCount': 'เล่น {count} เพลงถัดไป',
  'library.action.nextOneTitle': 'เล่น “{title}” เป็นเพลงถัดไป',
  'library.action.nextManyTitle': 'เล่นเพลงที่เลือกไว้ทั้ง {count} เพลงต่อจากนี้ ให้คนร้องคนเดียวกัน',

  'library.action.breakMark': 'ตั้งเป็นเพลงคั่น',
  'library.action.breakUnmark': 'เลิกเป็นเพลงคั่น',
  'library.action.breakMarkOneTitle': 'ใช้เพลงนี้เล่นคั่นระหว่างคนร้อง',
  'library.action.breakMarkManyTitle': 'ใช้ {count} เพลงนี้เล่นคั่นระหว่างคนร้อง',
  'library.action.breakUnmarkOneTitle': 'เลิกใช้เพลงนี้เล่นคั่นระหว่างคนร้อง',
  'library.action.breakUnmarkManyTitle': 'เลิกใช้ {count} เพลงนี้เล่นคั่นระหว่างคนร้อง',
  'library.action.breakMarkedTitle': 'ตั้งเป็นเพลงคั่นแล้ว',
  'library.action.breakUnmarkedTitle': 'เอาออกจากเพลงคั่นแล้ว',
  'library.action.breakUpdatedOne': 'อัปเดต {count} เพลง',
  'library.action.breakUpdatedMany': 'อัปเดต {count} เพลง',
  'library.action.breakFailed': 'อัปเดตเพลงคั่นไม่สำเร็จ',

  // MVP-354. "ลบ" is the plain word for delete; the sentences below say "ออกจากคลังเพลง" (out of
  // the catalogue) rather than a bare "ลบเพลง", because what happens is that the song leaves the
  // catalogue — the file stays — and an elderly operator reading the confirmation deserves the
  // distinction in the sentence itself, not in a footnote.
  'library.action.delete': 'ลบ',
  'library.action.deleteOneTitle': 'ลบ “{title}” ออกจากคลังเพลง',
  'library.action.deleteManyTitle': 'ลบ {count} เพลงออกจากคลังเพลง',
  'library.action.deletedTitle': 'ลบออกจากคลังเพลงแล้ว',
  'library.action.deletedOne': '“{title}” ไม่อยู่ในคลังเพลงแล้ว',
  'library.action.deletedMany': '{count} เพลงไม่อยู่ในคลังเพลงแล้ว',
  'library.action.deleteFailed': 'ลบไม่สำเร็จ',
  'library.action.restoreFailed': 'เลิกทำไม่สำเร็จ',
  'library.action.restoreFailedDetail': 'ยังมี {count} เพลงที่ถูกลบอยู่ ลองกู้คืนจากหน้าแอดมิน',
  'library.delete.confirmOne': 'ลบ “{title}” ออกจากคลังเพลงไหม',
  'library.delete.confirmMany': 'ลบ {count} เพลงออกจากคลังเพลงไหม',
  'library.delete.description': 'เพลงจะหายไปจากการค้นหา คลังเพลง และรายการเพลงคั่น ไฟล์ยังอยู่ และกดเลิกทำได้ทันทีหลังลบ',
  'library.delete.confirmAction': 'ลบ',

  'library.corrected': 'แสดงผลของ “{to}” แทน “{from}”',
  'library.results.label': 'รายการเพลง',
  'library.loadingMore': 'กำลังโหลดเพลงเพิ่ม…',

  'library.error.title': 'โหลดคลังเพลงไม่ได้',
  'library.empty.noMatchTitle': 'ไม่พบเพลงที่ตรง',
  'library.empty.noMatchDetail': 'ลองค้นด้วยคำอื่น หรือล้างตัวกรองดู',
  'library.empty.clearFilters': 'ล้างตัวกรอง',
  'library.empty.noSongsTitle': 'ยังไม่มีเพลง',
  'library.empty.noSongsDetail': 'นำเข้าคลังเพลงด้วย mv-ingest CLI แล้วรีเฟรชหน้านี้',
  'library.empty.refresh': 'รีเฟรช',

  'library.singer.title': 'ใครร้อง?',
  'library.singer.queueTitleOne': 'เข้าคิว: “{title}”',
  'library.singer.queueTitleMany': 'เข้าคิว: {count} เพลง',
  'library.singer.nextTitleOne': 'เล่นถัดไป: “{title}”',
  'library.singer.nextTitleMany': 'เล่นถัดไป: {count} เพลง',
  'library.singer.useAgain': 'ใช้ “{name}” อีกครั้ง',
  'library.singer.nameLabel': 'ชื่อคนร้อง',
  'library.singer.namePlaceholder': 'พิมพ์ชื่อ…',
  'library.singer.emptyHint': 'พิมพ์ชื่อ หรือเลือกจากรายการข้างล่าง',
  'library.singer.directoryLoading': 'กำลังโหลดรายชื่อคนร้อง…',
  // MVP-416, retitled by MVP-420. Not yet owner-reviewed.
  'library.singer.sungTonight': 'เพลงนี้ร้องไปแล้วในรอบนี้',
  'library.singer.sungTonightRow': '{name} ร้อง {song} ไปแล้ว · {when}',
  'library.singer.directoryError': 'โหลดรายชื่อคนร้องไม่สำเร็จ จึงยังเพิ่มคนร้องใหม่ไม่ได้ตอนนี้',
  'library.singer.useExisting': 'ใช้ {name}',
  'library.singer.useExistingNext': 'ใช้ {name} — เล่นถัดไป',
  'library.singer.addNew': 'เพิ่มคนร้องใหม่',
  'library.singer.addNewNext': 'เพิ่มคนร้องใหม่ — เล่นถัดไป',
  'library.queued.one': 'เข้าคิว “{title}” แล้ว',
  'library.queued.many': 'เข้าคิว {count} เพลงแล้ว',
  'library.queued.for': 'ให้ {name}',
  'library.queued.forNext': 'ให้ {name} เล่นเป็นเพลงถัดไป',
  'library.queued.failed': 'เพิ่มเข้าคิวไม่สำเร็จ',
  'library.queued.partial': 'เพิ่มไปได้ {done} จาก {total} เพลง แล้วหยุด {reason}',

  'settings.loadErrorTitle': 'โหลดการตั้งค่าไม่ได้',

  // MVP-396. Not yet owner-reviewed.
  'settings.tabsLabel': 'หมวดการตั้งค่า',
  // MVP-426 follow-up, owner's wording: this section is about how guests ask for songs — request
  // mode, songs per turn, turnaround, the message to the room. None of it is about a night.
  'settings.tonight': 'การขอเพลง',
  'settings.tonight.note': 'สี่อย่างนี้เปลี่ยนทุกกะ ตั้งได้จากหน้า Cockpit ตรงข้างรหัสเข้าร่วมด้วย',
  'settings.session.loadErrorTitle': 'โหลดรอบของคืนนี้ไม่ได้',
  'settings.session.none': 'ยังไม่ได้เปิดรอบ เปิดได้จากหน้าคอนโซล',
  'settings.session.requestMode': 'โหมดรับคำขอ',
  'settings.session.requestModeOpen': 'เปิด: คำขอเข้าคิวเลย',
  'settings.session.requestModeReview': 'ตรวจก่อน: อนุมัติทีละคำขอ',
  'settings.session.requestModeClosed': 'ปิด: คืนนี้ไม่รับคำขอ',
  'settings.session.songsPerTurn': 'จำนวนเพลงต่อรอบ',
  'settings.session.turnaround': 'เวลาสลับคน',
  // MVP-678: the venue passcode's one control. The rule lives in the label because ui/TextField
  // has no hint slot — see TonightSettings.tsx. "เว้นว่าง = ปิด" is the whole behaviour.
  'settings.session.passcode': 'รหัสผ่านร้าน (เว้นว่าง = ปิด)',
  'settings.session.passcodeRandom': 'สุ่ม',
  'settings.session.message': 'ข้อความถึงแขก',
  'settings.session.saveError': 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง',

  // MVP-472 follow-up 2. Not yet owner-reviewed.
  'settings.room': 'เครื่องนี้คุมห้อง',
  'settings.roomDefault': 'ห้องปกติ',
  'settings.room.note': 'จำไว้เฉพาะเครื่องนี้ เครื่องในห้องอื่นตั้งของตัวเองแยกกัน',
  'settings.room.confirmTitle': 'เปลี่ยนเครื่องนี้ไปคุม {room} ไหม',
  'settings.room.confirmBody': 'หน้าจะโหลดใหม่ เพลงที่เล่นอยู่บนเครื่องนี้จะหยุด คิว คำขอ และจอ Stage จะย้ายตามห้องใหม่',
  'settings.room.confirmAction': 'เปลี่ยนและโหลดใหม่',
  // MVP-472 follow-up 1. Not yet owner-reviewed.
  'settings.room.stageTheme': 'ธีมจอเวทีของห้องนี้',
  'settings.room.marquee': 'ข้อความวิ่งของห้องนี้',
  'settings.room.useVenue': 'ใช้ค่าของร้าน',
  'settings.room.overrideNote': 'เฉพาะห้องนี้ ปล่อยว่างไว้จะใช้ค่าจอเวทีของร้าน',

  'settings.appearance': 'การแสดงผล',
  'settings.language': 'ภาษา',
  'settings.theme': 'ธีม',
  'settings.themeDark': 'มืด',
  'settings.themeLight': 'สว่าง',
  // MVP-502. Not yet owner-reviewed. "Astryx" stays in Latin script in both dictionaries: it is a
  // product name, the same way the five Stage swatch names below are.
  'settings.palette': 'ชุดสี',
  'settings.paletteHouse': 'ของร้าน (เดิม)',
  'settings.paletteAstryx': 'Astryx',
  'settings.stageTheme': 'ธีมจอเวที',
  'settings.stageThemeAmber': 'Amber',
  'settings.stageThemeCyan': 'Cyan',
  'settings.stageThemeLime': 'Lime',
  'settings.stageThemeRose': 'Rose',
  'settings.stageThemeHighContrast': 'High contrast',

  'settings.stage': 'จอเวที',
  'settings.resetDefaults': 'คืนค่าเริ่มต้น',
  'settings.resetConfirmTitle': 'คืนค่าเริ่มต้นของ {section} หรือไม่?',
  'settings.resetConfirmDetail': 'การตั้งค่า {section} ทุกรายการในหน้านี้จะถูกแทนที่ด้วยค่าเริ่มต้น',

  'settings.ticker': 'ตัววิ่ง',
  'settings.ticker.enabled': 'แสดงตัววิ่ง',
  'settings.ticker.text': 'ข้อความตัววิ่ง',
  'settings.ticker.speed': 'ความเร็วตัววิ่ง',
  'settings.ticker.position': 'ตำแหน่งตัววิ่ง',
  'settings.ticker.positionTop': 'บน',
  'settings.ticker.positionBottom': 'ล่าง',

  'settings.attract': 'จอพัก',
  'settings.attract.mode': 'โหมดจอพัก',
  'settings.attract.modeStill': 'ภาพนิ่ง',
  'settings.attract.modeSlideshow': 'สไลด์',
  'settings.attract.interval': 'เวลาต่อสไลด์',
  'settings.attract.showJoinQr': 'แสดง QR สำหรับเข้าร่วม',
  'settings.attract.showRotation': 'แสดงคิวคนร้อง',
  'settings.attract.message': 'ข้อความบนจอพัก',

  'settings.overlays': 'ข้อความซ้อนจอ',
  'settings.overlays.nowPlaying': 'กำลังเล่น',
  'settings.overlays.upNext': 'ถัดไป',
  'settings.overlays.countdown': 'นับถอยหลัง',
  'settings.overlays.missingLyrics': 'ป้ายเตือนไม่มีเนื้อเพลง',
  'settings.overlays.missingLyricsNote': 'จะหรี่จอเวทีเมื่อเพลงไม่มีไฟล์เนื้อเพลง ถ้าวิดีโอมีเนื้อเพลงฝังอยู่แล้ว ให้ปิดไว้',
  'settings.overlays.duration': 'เวลาแสดงข้อความซ้อนจอ',

  'settings.dependent.ticker': 'เปิดตัววิ่งก่อน ถึงจะแก้ตรงนี้ได้',
  'settings.dependent.slideInterval': 'เปลี่ยนโหมดจอพักเป็นสไลด์ก่อน ถึงจะแก้ตรงนี้ได้',
  'settings.dependent.overlayDuration': 'เปิด “กำลังเล่น” หรือ “ถัดไป” ก่อน ถึงจะแก้ตรงนี้ได้',

  'settings.shortcuts': 'คีย์ลัด',
  // MVP-372: บอกทางออกด้วย ไม่ใช่แค่บอกให้กด
  'settings.shortcuts.pressKey': 'กดปุ่มที่ต้องการ… กด Esc หรือ Tab เพื่อใช้ปุ่มเดิม',
  'settings.shortcuts.cancel': 'ยกเลิก',
  'settings.shortcuts.change': 'เปลี่ยน',

  'settings.backup': 'สำรองข้อมูล',
  'settings.payment': 'การรับชำระเงิน',
  'settings.payment.note': 'บัญชีที่ร้านค้าโอนค่าแพ็กเกจเข้ามา ถ้าใส่พร้อมเพย์ ร้านค้าจะเห็น QR ก่อน ถ้าไม่ใส่จะเห็นเลขบัญชีธนาคาร',
  'settings.payment.promptPayId': 'พร้อมเพย์ (เบอร์มือถือหรือเลขประจำตัวผู้เสียภาษี)',
  'settings.payment.save': 'บันทึก',
  'settings.payment.saved': 'บันทึกแล้ว ร้านค้าจะเห็นบัญชีนี้ในหน้าแพ็กเกจ',
  'settings.payment.failed': 'บันทึกบัญชีรับเงินไม่สำเร็จ',
  'settings.backup.export': 'ส่งออกการตั้งค่า',
  'settings.backup.import': 'นำเข้าการตั้งค่า',
  'settings.import.notJson': 'ไฟล์นี้ไม่ใช่ JSON ที่ถูกต้อง',
  'settings.import.notOurs': 'ไฟล์นี้ไม่ใช่ไฟล์ส่งออกการตั้งค่าของ RMS Karaoke',
  'settings.import.empty': 'ไฟล์นี้ไม่มีการตั้งค่าอยู่เลย',
  // Not yet owner-reviewed.
  'settings.import.unreadable': 'อ่านไฟล์นี้ไม่ได้ ลองเลือกไฟล์ใหม่อีกครั้ง',
  'settings.importConfirmTitle': 'นำเข้าการตั้งค่าจาก {filename} หรือไม่?',
  'settings.importConfirmDetailOne': 'การทำเช่นนี้จะเขียนทับ 1 รายการด้วยค่าจากไฟล์นั้น',
  'settings.importConfirmDetailMany': 'การทำเช่นนี้จะเขียนทับ {count} รายการด้วยค่าจากไฟล์นั้น',
  'settings.toastExported': 'ส่งออกการตั้งค่าเป็น {filename} แล้ว',
  'settings.toastImported': 'นำเข้าการตั้งค่าจาก {filename} แล้ว',

  'settings.unit.sec': 'วิ',
  'settings.unit.pxPerSec': 'px/s',

  // MVP-272 Phase 6 (the three admin screens). Plainer Thai than the English: "invite link" is
  // "ลิงก์เชิญ", and the three roles keep the venue's own words rather than Strapi's.

  'admin.activity.title': 'บันทึกกิจกรรม',
  'admin.activity.refresh': 'รีเฟรช',
  'admin.activity.loadMore': 'โหลดเพิ่ม',
  'admin.activity.emptyTitle': 'ยังไม่มีรายการ',
  'admin.activity.emptyDetail': 'ลิงก์เชิญ การแก้ไขบัญชี และการเข้าสู่ระบบ จะขึ้นที่นี่',
  'admin.activity.loadError': 'โหลดบันทึกกิจกรรมไม่ได้',
  // MVP-444. Not yet owner-reviewed.
  'admin.activity.loadingMore': 'กำลังโหลดรายการเพิ่ม…',
  'admin.activity.moreError': 'โหลดหน้าถัดไปไม่ได้',
  'admin.activity.filters': 'กรอง',
  'admin.activity.filterAction': 'เรื่องที่เกิด',
  'admin.activity.filterActionAny': 'ทุกเรื่อง',
  'admin.activity.filterActor': 'คนที่ทำ',
  'admin.activity.filterActorAny': 'ทุกคน',
  'admin.activity.filterCount': 'ตรงกับตัวกรอง {count} รายการ',
  'admin.activity.filterClear': 'ล้างตัวกรอง',
  'admin.activity.filterEmptyTitle': 'ไม่มีรายการตรงกับตัวกรองนี้',
  'admin.activity.filterEmptyDetail': 'ลองเปลี่ยนเรื่องหรือคน หรือล้างตัวกรองเพื่อดูทั้งหมด',
  'admin.audit.action.inviteCreate': 'ออกลิงก์เชิญ',
  'admin.audit.action.inviteRevoke': 'ยกเลิกลิงก์เชิญ',
  'admin.audit.action.inviteRedeem': 'ใช้ลิงก์เชิญ',
  'admin.audit.action.accountUpdate': 'แก้ไขบัญชี',
  'admin.audit.action.loginSuccess': 'เข้าสู่ระบบ',
  'admin.audit.action.loginLocked': 'ถูกล็อกชั่วคราว',
  'admin.audit.action.loginRefused': 'เข้าสู่ระบบไม่ผ่าน',
  'admin.audit.action.sessionJoinCodeRotated': 'เปลี่ยนรหัสเข้าร่วม',
  'admin.audit.action.passwordChange': 'เปลี่ยนรหัสผ่าน',
  'admin.audit.action.songDownload': 'ดาวน์โหลดไฟล์เพลง',
  'admin.audit.action.songDelete': 'ลบเพลง',
  'admin.audit.action.songRestore': 'กู้คืนเพลง',
  // MVP-498. Not yet owner-reviewed.
  'admin.audit.action.sessionRoomMoved': 'ย้ายห้อง',
  // MVP-652. Not yet owner-reviewed.
  'admin.audit.action.sessionScopeRefused': 'ถูกปฏิเสธ: สาขาอื่น',

  // MVP-358 — เพลงที่ถูกเก็บ (ลบหรือซ่อน)
  'admin.withheld.title': 'เพลงที่ถูกเก็บ',
  'admin.withheld.description': 'เพลงที่มีคนเอาออกจากคลัง — ลบหรือซ่อน แตะ กู้คืน เพื่อเอากลับเข้าคลัง',
  'admin.withheld.refresh': 'รีเฟรช',
  'admin.withheld.emptyTitle': 'ไม่มีเพลงที่ถูกเก็บ',
  'admin.withheld.emptyDetail': 'ตอนนี้เพลงทุกเพลงในคลังค้นหาเจอ',
  'admin.withheld.loadError': 'โหลดรายการเพลงที่ถูกเก็บไม่ได้',
  'admin.withheld.badge.deleted': 'ลบแล้ว',
  'admin.withheld.badge.hidden': 'ซ่อนอยู่',
  'admin.withheld.restore': 'กู้คืน',
  'admin.withheld.restoreAria': 'กู้คืน {title}',
  // MVP-437. Not yet owner-reviewed.
  'admin.withheld.restoreToHidden': 'กู้คืนเป็นซ่อน',
  'admin.withheld.restoreToHiddenAria': 'กู้คืน {title} เป็นซ่อน — ยังค้นหาไม่เจอ',
  'admin.withheld.restoredToHidden':
    '“{title}” กู้คืนแล้ว แต่ยังซ่อนอยู่ — ก่อนถูกลบเพลงนี้ซ่อนอยู่ ตอนนี้จึงยังค้นหาไม่เจอ กดกู้คืนอีกครั้งเพื่อเอาเข้าคลัง',
  'admin.withheld.restoring': 'กำลังกู้คืน…',
  'admin.withheld.restoreError': 'กู้คืนเพลงนี้ไม่ได้',
  'admin.withheld.deletedBy': 'ลบโดย {actor}',
  'admin.withheld.deletedAt': 'ลบเมื่อ {when}',
  // MVP-458. Not yet owner-reviewed.
  'admin.withheld.dayUnknown': 'ไม่มีวันที่บันทึกไว้',
  // MVP-443. Not yet owner-reviewed.
  'admin.withheld.loadingMore': 'กำลังโหลดเพลงเพิ่ม…',
  'admin.withheld.moreError': 'โหลดหน้าถัดไปไม่ได้',
  'admin.withheld.filters': 'กรอง',
  'admin.withheld.filterFrom': 'เก็บตั้งแต่วันที่',
  'admin.withheld.filterTo': 'เก็บถึงวันที่',
  // MVP-458. Not yet owner-reviewed.
  'admin.withheld.filterSearch': 'ค้นหา',
  'admin.withheld.filterSearchHint': 'ชื่อเพลง ศิลปิน หรือรหัส',
  'admin.withheld.filterActor': 'ลบโดย',
  'admin.withheld.filterActorAny': 'ทุกคน',
  'admin.withheld.filterClear': 'ล้างตัวกรอง',
  'admin.withheld.filterEmptyTitle': 'ไม่มีเพลงตรงกับตัวกรองนี้',
  'admin.withheld.filterEmptyDetail': 'ขยายช่วงวันที่ หรือล้างตัวกรองเพื่อดูเพลงที่ถูกเก็บทั้งหมด',
  // MVP-458. Not yet owner-reviewed.
  'admin.withheld.backToTop': 'กลับขึ้นบนสุด',

  'admin.audit.signedOut': 'ไม่ได้เข้าสู่ระบบ',
  'admin.audit.anAccount': 'บัญชีหนึ่ง',
  'admin.audit.inviteCreate': 'สร้างลิงก์เชิญสำหรับ {role}',
  'admin.audit.inviteCreateLabelled': 'สร้างลิงก์เชิญสำหรับ {role} ({label})',
  'admin.audit.inviteRevoke': 'ยกเลิกลิงก์เชิญสำหรับ {role}',
  'admin.audit.inviteRevokeLabelled': 'ยกเลิกลิงก์เชิญสำหรับ {role} ({label})',
  'admin.audit.inviteRedeem': 'สร้างบัญชีจากลิงก์เชิญ เป็น {role}',
  'admin.audit.accountUpdate': 'แก้ไข {target}: {changes}',
  'admin.audit.accountUpdateUnnamed': 'แก้ไขบัญชีหนึ่ง: {changes}',
  'admin.audit.change': '{field} {from} → {to}',
  'admin.audit.changeNothing': 'ไม่มีอะไรเปลี่ยน',
  'admin.audit.loginSuccess': 'เข้าสู่ระบบ',
  'admin.audit.loginRefused': 'ปฏิเสธ: {reason}',
  'admin.audit.refusedNotYetValid': 'ยังไม่ถึงวันเริ่มใช้งาน',
  'admin.audit.refusedExpired': 'หมดอายุการใช้งานแล้ว',
  'admin.audit.refusedOutsideWindow': 'อยู่นอกช่วงเวลาที่ใช้งานได้',
  'admin.audit.loginLocked': 'ลองเข้าสู่ระบบผิดหลายครั้ง — ถูกล็อก',
  'admin.audit.loginLockedFor': 'ลองเข้าสู่ระบบผิดหลายครั้งด้วย "{target}" — ถูกล็อก',
  'admin.audit.joinCodeRotated': 'เปลี่ยนรหัสเข้าร่วม',
  // MVP-498. Not yet owner-reviewed. Room IDs, not names: the log is a snapshot of what happened.
  'admin.audit.sessionRoomMoved': 'ย้าย "{target}" จากห้อง {fromRoom} ไปห้อง {toRoom}',
  'admin.audit.sessionUnnamed': 'รอบร้องนี้',
  // MVP-652. Not yet owner-reviewed. The session id is shown as-is — this screen is admin-only.
  'admin.audit.sessionScopeRefused': 'ถูกปฏิเสธ เพราะเรียกรอบร้องของสาขาอื่น ({route}, รอบร้อง {sessionId})',
  'admin.audit.joinCodeRotatedFor': 'เปลี่ยนรหัสเข้าร่วมของ {target}',
  'admin.audit.passwordChange': 'เปลี่ยนรหัสผ่านของตัวเอง',
  'admin.audit.field.roleType': 'สิทธิ์',
  'admin.audit.field.blocked': 'ระงับ',
  'admin.audit.field.displayName': 'ชื่อ',
  'admin.audit.field.validFrom': 'ใช้งานตั้งแต่',
  'admin.audit.field.validUntil': 'ใช้งานถึง',
  'admin.audit.value.none': 'ไม่กำหนด',
  'admin.audit.value.blank': 'ว่าง',
  'admin.audit.value.yes': 'ใช่',
  'admin.audit.value.no': 'ไม่',

  'admin.role.customer': 'ลูกค้า',
  'admin.role.customerHint': 'ดูแลห้องได้ทั้งหมด: คลังเพลง ห้องคุม คิว และตั้งค่า',
  // MVP-464. Not yet owner-reviewed.
  'admin.role.shop': 'ร้าน',
  'admin.role.shopHint': 'ดูแลห้องได้ทั้งหมด และชวนพนักงานของตัวเองเข้าใช้ได้ ดาวน์โหลดไม่ได้',
  'admin.role.agency': 'ตัวแทน',
  'admin.role.agencyHint': 'ดูแลห้องได้ทั้งหมด ชวนคนเข้าใช้ไม่ได้ และดาวน์โหลดไม่ได้',
  'admin.role.downloader': 'ผู้ดาวน์โหลด',
  'admin.role.downloaderHint': 'ดูคลังเพลงและดาวน์โหลดเท่านั้น ไม่มีคิวและการเล่น',
  'admin.role.admin': 'ผู้ดูแลระบบ',
  'admin.role.adminHint': 'ทำได้ทุกอย่างเหมือนลูกค้า บวกนำเข้าเพลงและจัดการบัญชีเหล่านี้',
  // MVP-585. Not yet owner-reviewed. 'ตัวแทนลิขสิทธิ์' is deliberately longer than the existing
  // 'ตัวแทน' (agency) so the two never read as the same role in a list.
  'admin.role.rights': 'ตัวแทนลิขสิทธิ์',
  'admin.role.rightsHint': 'ดูรายงานค่าลิขสิทธิ์ได้อย่างเดียว เห็นยอดรวมทุกร้าน ไม่เห็นรายร้าน',

  // MVP-585. Not yet owner-reviewed.
  'rights.report.title': 'รายงานค่าลิขสิทธิ์',
  'rights.report.period': 'เดือน',
  'rights.report.belowThreshold': 'ต่ำกว่าเกณฑ์รายงาน (รวมกัน)',
  'rights.report.empty': 'เดือนนี้ไม่มียอดการเล่นที่รายงานได้',

  'admin.window.permanent': 'ถาวร',
  'admin.window.until': 'ถึง {date}',
  'admin.window.from': 'ตั้งแต่ {date}',
  'admin.window.expired': 'หมดอายุ {date}',

  'admin.invite.statusUsed': 'ใช้แล้วโดย {name}',
  'admin.invite.statusUsedUnknown': 'ใช้แล้ว',
  'admin.invite.statusRevoked': 'ยกเลิกแล้ว',
  'admin.invite.statusExpired': 'ลิงก์หมดอายุ',
  'admin.invite.statusActive': 'ใช้งานได้',
  'admin.invite.statusValidUntil': 'ลิงก์ใช้ได้ถึง {date}',

  'admin.invite.linksTitle': 'ลิงก์เชิญ',
  // MVP-418. Not yet owner-reviewed.
  'admin.invite.colLink': 'ลิงก์',
  'admin.invite.colStatus': 'สถานะ',
  'admin.invite.colGrants': 'ให้สิทธิ์',
  'admin.invite.emptyTitle': 'ยังไม่มีลิงก์เชิญ',
  'admin.invite.emptyDetail': 'สร้างด้านบน แล้วส่งให้คนที่จะเข้าร่วม',
  'admin.invite.roleLabel': 'สิทธิ์ของลิงก์เชิญนี้',
  // MVP-655. Not yet owner-reviewed. ใช้คำเดิมของแอป: สาขา = venue (ดู admin.staff.branch, server.venueNotInScope)
  'admin.invite.venueLabel': 'สาขาที่ร้านนี้ดูแล',
  'admin.invite.venueUnset': 'เลือกสาขา…',
  'admin.invite.noteLabel': 'หมายเหตุ (ไม่บังคับ)',
  'admin.invite.notePlaceholder': 'เช่น ห้อง 3',
  // MVP-465. Not yet owner-reviewed.
  'admin.invite.accessFrom': 'ใช้งานตั้งแต่',
  'admin.invite.accessUntil': 'ใช้งานถึง',
  // MVP-465. Not yet owner-reviewed.
  'admin.invite.permanent': 'ใช้งานถาวร',
  'admin.invite.create': 'สร้างลิงก์เชิญ',
  // MVP-465. Not yet owner-reviewed.
  'admin.invite.formHint':
    '{roleHint} เว้น "ใช้งานตั้งแต่" ว่างไว้ ถ้าจะให้เริ่มใช้ได้ทันทีที่กดรับลิงก์ และติ๊ก "ใช้งานถาวร" ถ้าจะให้ใช้งานได้ไม่มีวันหมด ตัวลิงก์เองหยุดทำงานใน 14 วัน ไม่ว่าจะถูกใช้แล้วหรือยัง',
  'admin.invite.oneTimeNotice': 'ส่งลิงก์นี้ — แสดงครั้งเดียว ย้อนดูอีกไม่ได้:',
  'admin.invite.copy': 'คัดลอก',
  'admin.invite.copied': 'คัดลอกแล้ว',
  'admin.invite.grants': 'ให้สิทธิ์: {window}',
  'admin.invite.revoke': 'ยกเลิก',
  'admin.invite.revokeConfirmTitle': 'ยกเลิกลิงก์เชิญของ {target} หรือไม่?',
  'admin.invite.revokeConfirmDetail': 'ลิงก์นี้จะหยุดทำงานทันที แม้ยังไม่มีใครใช้',
  'admin.invite.createError': 'สร้างลิงก์เชิญไม่ได้',
  'admin.invite.revokeError': 'ยกเลิกลิงก์นั้นไม่ได้',
  'admin.invite.toastRevoked': 'ยกเลิกลิงก์เชิญของ {target} แล้ว',

  'admin.accounts.title': 'บัญชีผู้ใช้',
  'admin.accounts.emptyTitle': 'ยังไม่มีบัญชี',
  'admin.accounts.emptyDetail': 'ยังไม่มีใครใช้ลิงก์เชิญ',
  'admin.accounts.you': 'คุณ',
  // MVP-419. Not yet owner-reviewed.
  'admin.accounts.tabsLabel': 'ผู้ใช้และลิงก์เชิญ',
  // MVP-418. Not yet owner-reviewed.
  'admin.accounts.blockedBadge': 'ถูกระงับ',
  'admin.accounts.editWindow': 'แก้ช่วงเวลา',
  'admin.accounts.colUser': 'บัญชี',
  'admin.accounts.colRole': 'สิทธิ์',
  'admin.accounts.colAccess': 'ช่วงเวลาใช้งาน',
  'admin.accounts.roleLabel': 'สิทธิ์ของ {name}',
  'admin.accounts.from': 'ตั้งแต่',
  'admin.accounts.until': 'ถึง',
  'admin.accounts.save': 'บันทึก',
  'admin.accounts.block': 'ระงับ',
  'admin.accounts.unblock': 'เลิกระงับ',
  'admin.accounts.blockConfirmTitle': 'ระงับ {name} หรือไม่?',
  'admin.accounts.blockConfirmDetail': '{name} จะเข้าสู่ระบบไม่ได้จนกว่าจะเลิกระงับ',
  'admin.accounts.unblockConfirmTitle': 'เลิกระงับ {name} หรือไม่?',
  'admin.accounts.unblockConfirmDetail': '{name} จะกลับมาเข้าสู่ระบบได้อีกครั้ง',
  // MVP-510 slice 4: the staff roster. Not yet owner-reviewed. ร้าน = the venue and สาขา = the
  // branch, the pair the Licences and Rooms screens already use, so a shop with three branches
  // reads the same two words on all three screens.
  'admin.staff.title': 'พนักงาน',
  'admin.staff.emptyTitle': 'ยังไม่มีบัญชีพนักงาน',
  'admin.staff.emptyDetail': 'ยังไม่มีใครใช้ลิงก์เชิญของร้าน',
  'admin.staff.colBranch': 'สาขา',
  'admin.accounts.loadError': 'โหลดรายชื่อบัญชีไม่ได้',
  'admin.accounts.saveError': 'บันทึกการแก้ไขไม่ได้',
  'admin.accounts.toastRoleChanged': 'ตั้งสิทธิ์ของ {name} เป็น {role} แล้ว',
  'admin.accounts.toastWindowChanged': 'บันทึกช่วงเวลาการใช้งานของ {name} แล้ว',
  'admin.accounts.toastRoleAndWindowChanged': 'อัปเดตสิทธิ์และช่วงเวลาการใช้งานของ {name} แล้ว',
  'admin.accounts.toastBlocked': 'ระงับ {name} แล้ว',
  'admin.accounts.toastUnblocked': 'เลิกระงับ {name} แล้ว',

  'admin.ingest.choose': 'เลือกไฟล์…',
  'admin.ingest.uploading': 'กำลังอัปโหลด…',
  'admin.ingest.uploadOne': 'อัปโหลด 1 ไฟล์',
  'admin.ingest.uploadMany': 'อัปโหลด {count} ไฟล์',
  'admin.ingest.clear': 'ล้างรายการ',
  'admin.ingest.emptyTitle': 'ยังไม่ได้เลือกไฟล์',
  'admin.ingest.emptyDetail':
    'เลือกไฟล์วิดีโอหนึ่งไฟล์หรือมากกว่า เว้นรหัสเพลงว่างไว้ ระบบจะให้หมายเลขถัดไปอัตโนมัติ ถ้าจะนำเข้าทั้งโฟลเดอร์ ให้ใช้ ingest CLI แทน มันวัดความดังและตัดความเงียบท้ายไฟล์ให้ด้วย',
  'admin.ingest.codeLabel': 'รหัสเพลงของ {file}',
  'admin.ingest.codePlaceholder': 'อัตโนมัติ',
  'admin.ingest.titleLabel': 'ชื่อเพลงของ {file}',
  'admin.ingest.titlePlaceholder': 'ชื่อเพลง',
  'admin.ingest.artistLabel': 'ศิลปินของ {file}',
  'admin.ingest.artistPlaceholder': 'ศิลปิน',
  'admin.ingest.uploadingFile': 'กำลังอัปโหลด {file}',
  'admin.ingest.summary': 'เพิ่มแล้ว {done} ไฟล์',
  'admin.ingest.summaryWithFailed': 'เพิ่มแล้ว {done} ไฟล์ ล้มเหลว {failed} ไฟล์',
  'admin.ingest.status.waiting': 'รอ',
  'admin.ingest.status.hashing': 'กำลังคำนวณ hash…',
  'admin.ingest.status.finishing': 'กำลังปิดงาน…',
  'admin.ingest.status.uploading': 'กำลังอัปโหลด…',
  'admin.ingest.status.added': 'เพิ่มแล้ว',
  'admin.ingest.status.addedAs': 'เพิ่มแล้วเป็น {code}',
  'admin.ingest.status.failed': 'ล้มเหลว',
  'admin.ingest.status.cancelled': 'หยุดก่อนไฟล์นี้จะเสร็จ',
  'admin.ingest.needTitle': 'ใส่ชื่อเพลงให้ไฟล์นี้ก่อนอัปโหลด',
  'admin.ingest.badCatalogId': 'รหัสเพลงใช้ได้เฉพาะตัวอักษร ตัวเลข และขีดกลาง',
  'admin.ingest.clearConfirmOne': 'ล้าง 1 ไฟล์ออกจากรายการหรือไม่',
  'admin.ingest.clearConfirmMany': 'ล้าง {count} ไฟล์ออกจากรายการหรือไม่',
  'admin.ingest.clearConfirmDetail': 'ชื่อเพลง ศิลปิน หรือรหัสเพลงที่พิมพ์ไว้จะหายไปทั้งหมด',
  // MVP-336
  'admin.ingest.stop': 'หยุด',
  'admin.ingest.batchProgress': 'เสร็จแล้ว {done} จาก {total} · กำลังอัปโหลด {file}',
  'admin.ingest.summaryWithCancelled': 'เพิ่มแล้ว {done} ล้มเหลว {failed} หยุด {cancelled}',
  'admin.ingest.showDetail': 'รายละเอียดทางเทคนิค',
  // MVP-339
  'admin.ingest.whatToTry': 'ลองแบบนี้',

  'admin.ingest.fail.network': 'ติดต่อเซิร์ฟเวอร์ไม่ได้ ตรวจอินเทอร์เน็ตแล้วอัปโหลดใหม่',
  'admin.ingest.fail.denied': 'สิทธิ์เพิ่มเพลงของคุณหมดแล้ว เข้าสู่ระบบใหม่อีกครั้ง',
  'admin.ingest.fail.diskFull': 'เซิร์ฟเวอร์ไม่มีที่ว่างพอสำหรับไฟล์นี้ ลบไฟล์เก่าออกก่อนแล้วอัปโหลดใหม่',
  'admin.ingest.fail.rejectedSize': 'เซิร์ฟเวอร์ปฏิเสธไฟล์นี้เพราะใหญ่เกินไป ใช้ ingest CLI เพิ่มแทน',
  'admin.ingest.fail.corrupt': 'ไฟล์ที่ส่งไปเสียหายระหว่างทาง อัปโหลดใหม่อีกครั้ง',
  'admin.ingest.fail.storeFailed': 'เซิร์ฟเวอร์เก็บไฟล์ไม่สำเร็จ ลองใหม่อีกสักครู่',
  'admin.ingest.fail.server': 'เซิร์ฟเวอร์มีปัญหากับไฟล์นี้ ลองใหม่อีกสักครู่',
  'admin.ingest.fail.empty': 'ไฟล์นี้ว่างเปล่า ตรวจไฟล์ต้นทางที่ก๊อปมา แล้วเลือกใหม่',
  'admin.ingest.fail.tooLarge': 'ไฟล์นี้เกิน {limit} MB ใช้ ingest CLI เพิ่มแทน',
  'admin.ingest.fail.unreadable': 'อ่านไฟล์นี้เป็นวิดีโอไม่ได้ ลองเปิดดูว่าเล่นได้ไหมแล้วค่อยลองใหม่',
  'admin.ingest.fail.unknown': 'เพิ่มไฟล์นี้ไม่ได้ ลองใหม่อีกครั้ง ถ้ายังไม่ได้ ส่งรายละเอียดข้างล่างไปด้วย',

  'auth.redeem.joiningAs': 'เข้าร่วมในสิทธิ์ {role}',
  'auth.redeem.accessPermanent': 'ใช้งานได้ไม่มีวันหมด',
  'auth.redeem.access': 'ใช้งานได้: {window}',
  'auth.redeem.usernameTooShort': 'ตั้งชื่อผู้ใช้อย่างน้อย 3 ตัวอักษร',
  'auth.redeem.emailInvalid': 'กรอกอีเมลให้ถูกต้อง',
  'auth.redeem.passwordTooShort': 'ตั้งรหัสผ่านอย่างน้อย 10 ตัวอักษร',
  'auth.redeem.passwordMismatch': 'รหัสผ่านสองช่องไม่ตรงกัน',

  // MVP-272 Phase 7 (เข้าสู่ระบบ / รับคำเชิญ / บัญชีของตัวเอง).
  'common.serverUnreachableRetry': 'ติดต่อเซิร์ฟเวอร์ไม่ได้ ตรวจการเชื่อมต่อแล้วลองใหม่',
  'auth.passwordMinHint': 'อย่างน้อย {min} ตัวอักษร',

  // Identical to the English on purpose — a registered company name. See `messages.en.ts`.
  'brand.logoAlt': 'RMS Publishing',

  'auth.login.subtitle': 'เข้าสู่ระบบเพื่อเปิดงานคืนนี้',
  'auth.login.identifier': 'อีเมลหรือชื่อผู้ใช้',
  'auth.login.password': 'รหัสผ่าน',
  'auth.login.submit': 'เข้าสู่ระบบ',
  'auth.login.badCredentials': 'อีเมล/ชื่อผู้ใช้ หรือรหัสผ่านไม่ถูกต้อง ลองอีกครั้ง',
  // MVP-645. Not yet owner-reviewed. ใช้เมื่อเซิร์ฟเวอร์ตอบปฏิเสธมาด้วยเหตุผลที่บิลด์นี้ยังไม่มีประโยคให้
  // เดิมไปใช้ common.serverUnreachableRetry ซึ่งบอกว่าติดต่อเซิร์ฟเวอร์ไม่ได้ทั้งที่เซิร์ฟเวอร์เพิ่งตอบกลับมา
  'auth.login.refused': 'เซิร์ฟเวอร์ไม่อนุญาตให้บัญชีนี้เข้าสู่ระบบ กรุณาแจ้งเจ้าของร้านหรือคนที่ตั้งบัญชีให้ตรวจสอบบัญชีนี้',
  // MVP-471. Not yet owner-reviewed.
  'auth.login.challengeRequired': 'เข้าสู่ระบบผิดหลายครั้ง กรุณายืนยันว่าไม่ใช่บอทด้านล่าง แล้วลองใหม่',
  // MVP-645. Not yet owner-reviewed. คนละเรื่องกับ challengeRequired — อันนี้คือตัวยืนยันตัวตนใช้งานไม่ได้เอง
  // คนหน้าเครื่องไม่ได้ทำอะไรผิดและแก้เองไม่ได้ ปัญหาอยู่ฝั่งเซิร์ฟเวอร์ จึงบอกว่าให้โทรหาฝ่ายสนับสนุน
  'auth.login.challengeUnavailable': 'ตอนนี้ระบบยืนยันว่าไม่ใช่บอทใช้งานไม่ได้ จึงเข้าสู่ระบบไม่ได้ กรุณารอสักครู่แล้วลองอีกครั้ง ถ้ายังเป็นอยู่ให้ติดต่อฝ่ายสนับสนุน',
  'auth.sessionExpired': 'เซสชันของคุณหมดอายุ กรุณาเข้าสู่ระบบอีกครั้งเพื่อดำเนินการต่อ',

  'auth.invite.subtitle': 'คุณได้รับคำเชิญ ตั้งวิธีเข้าสู่ระบบของคุณ',
  'auth.invite.subtitleDone': 'บัญชีของคุณพร้อมใช้งานแล้ว',
  'auth.invite.linkInvalid':
    'ลิงก์เชิญนี้ใช้ไม่ได้แล้ว อาจถูกใช้ไปแล้ว ถูกยกเลิก หรือหมดอายุ — ขอลิงก์ใหม่จากคนที่ส่งให้',
  'auth.invite.checkFailed': 'ตรวจลิงก์เชิญนี้ไม่ได้ อีกสักครู่ลองใหม่',
  'auth.invite.checking': 'กำลังตรวจลิงก์เชิญ…',
  'auth.invite.created': 'สร้างบัญชี “{name}” แล้ว ใช้บัญชีนี้เข้าสู่ระบบจากนี้ไป',
  'auth.invite.goToSignIn': 'ไปหน้าเข้าสู่ระบบ',
  'auth.invite.createFailed': 'สร้างบัญชีไม่ได้ ตรวจการเชื่อมต่อแล้วลองใหม่',
  'auth.invite.username': 'ชื่อผู้ใช้',
  'auth.invite.email': 'อีเมล',
  'auth.invite.password': 'รหัสผ่าน',
  'auth.invite.passwordAgain': 'รหัสผ่านอีกครั้ง',
  'auth.invite.submit': 'สร้างบัญชีของฉัน',

  'account.title': 'บัญชีของคุณ',
  'account.username': 'ชื่อผู้ใช้',
  'account.email': 'อีเมล',
  'account.role': 'สิทธิ์',
  'account.askAdmin': 'ถ้าต้องเปลี่ยนอีเมล ชื่อผู้ใช้ หรือสิทธิ์ ให้บอกผู้ดูแลระบบ',
  'account.displayName': 'ชื่อที่แสดง',
  'account.displayNameSaved': 'บันทึกชื่อที่แสดงแล้ว',
  'account.saveDisplayName': 'บันทึกชื่อที่แสดง',
  'account.saveFailed': 'บันทึกไม่ได้ ตรวจการเชื่อมต่อแล้วลองใหม่',
  'account.password': 'รหัสผ่าน',
  'account.passwordChanged': 'เปลี่ยนรหัสผ่านแล้ว',
  'account.currentPassword': 'รหัสผ่านปัจจุบัน',
  'account.newPassword': 'รหัสผ่านใหม่',
  'account.newPasswordAgain': 'รหัสผ่านใหม่อีกครั้ง',
  'account.changePassword': 'เปลี่ยนรหัสผ่าน',
  'account.displayNameTooLong': 'ชื่อที่แสดงยาวได้ไม่เกิน {max} ตัวอักษร',
  'account.needCurrentPassword': 'กรอกรหัสผ่านปัจจุบัน',
  'account.newPasswordTooShort': 'ตั้งรหัสผ่านใหม่อย่างน้อย {min} ตัวอักษร',
  'account.passwordMismatch': 'รหัสผ่านใหม่สองช่องไม่ตรงกัน',
  'account.passwordUnchanged': 'รหัสผ่านใหม่ซ้ำกับรหัสผ่านปัจจุบัน',
  'account.wrongCurrentPassword': 'รหัสผ่านปัจจุบันไม่ถูกต้อง',
  'account.tooManyAttempts': 'ลองมากเกินไป รอสักนาทีแล้วลองใหม่',
  'account.role.guest': 'แขก',
  'account.role.signedOut': 'ยังไม่เข้าสู่ระบบ',

  // MVP-289. Same bare word everywhere it appears, so one Thai word each rather than a sentence —
  // see `messages.en.ts`'s comment for why these are `common.` and not owned by any one surface.
  'common.close': 'ปิด',
  // MVP-424. No space before `{label}`: Thai does not separate words, and every label these wrap is
  // itself Thai. Not yet owner-reviewed.
  'common.decrease': 'ลด{label}',
  'common.increase': 'เพิ่ม{label}',
  'common.dismiss': 'ปิด',
  'common.loading': 'กำลังโหลด',
  'common.mainNavigation': 'เมนูหลัก',
  // MVP-357: `SearchField`'s clear button. `common.` rather than `library.search.clear`
  // because it names the same bare action on any field that has one — the guest page's own
  // `guest.search.clear` predates this and stays where it is, since it sits in a block that
  // ships to a separate bundle.
  'common.clearSearch': 'ล้างคำค้นหา',
  // MVP-354: same word `rotation.undo` already uses — one toast button, two characters, and the
  // one an operator would actually say.
  'common.undo': 'เลิกทำ',
  // MVP-497. Not yet owner-reviewed. "เต็มจอ" is the everyday word for this on a Thai UI — never
  // "โหมดเต็มหน้าจอ", which is a manual's phrasing for a one-word button.
  'common.fullscreen.enter': 'เต็มจอ',
  'common.fullscreen.exit': 'ออกจากเต็มจอ',

  // MVP-371: คำที่ UI พูดเมื่อ backend ของเราปฏิเสธด้วย code — ไม่ใช่ข้อความอังกฤษจากสายอีกต่อไป
  'account.passwordChangeFailed': 'เปลี่ยนรหัสผ่านไม่สำเร็จ ลองใหม่อีกครั้ง',
  // MVP-390. Not yet owner-reviewed.
  'server.offline': 'ติดต่อ {host} ไม่ได้ ตรวจการเชื่อมต่อแล้วลองใหม่',
  'server.unreachable': 'เซิร์ฟเวอร์ {host} ไม่ตอบ (อาจกำลังรีสตาร์ต) รอสักครู่แล้วลองใหม่',
  'server.authRequired': 'เข้าสู่ระบบใหม่ก่อนทำรายการนี้',
  'server.rateLimited': 'ทำรายการถี่เกินไป รอสักครู่แล้วลองใหม่',
  'server.sessionNotFound': 'รอบนี้ไม่ได้เปิดอยู่แล้ว',
  // MVP-567. Not yet owner-reviewed. คนละเรื่องกับ sessionNotFound ด้านบน อันนั้นคือรอบที่เคยเปิดแล้วปิดไป
  // อันนี้คือห้องนี้ยังไม่มีรอบเปิดเลย บอกสิ่งที่เกิดก่อน แล้วค่อยบอกว่าต้องทำอะไร
  'server.noActiveSession': 'ห้องนี้ยังไม่ได้เปิดรอบ เปิดรอบของห้องนี้ก่อนแล้วลองใหม่',
  'server.nothingToUndo': 'ไม่มีอะไรให้ย้อนกลับแล้ว',
  'server.nothingToRedo': 'ไม่มีอะไรให้ทำซ้ำแล้ว',
  'server.nothingToMerge': 'เลือกเพลงที่จะเก็บไว้ และอีกอย่างน้อยหนึ่งเพลงในกลุ่มที่จะรวมเข้าไป',
  'server.patchEmpty': 'ไม่มีอะไรเปลี่ยน',
  'server.dateInvalid': 'วันที่ไม่ถูกต้อง',
  'server.phaseInvalid': 'รอบนี้อยู่ในสถานะนั้นไม่ได้',
  'server.requestAlreadyDecided': 'คำขอนี้มีคนตัดสินไปแล้ว',
  'server.requestNotRestorable': 'เอากลับได้เฉพาะคำขอที่ถูกปฏิเสธ',
  'server.requestTooOldToRestore': 'คำขอนี้เก่าเกินไป เอากลับมาก็จะหมดอายุทันที',
  'server.requestsClosed': 'คืนนี้ปิดรับคำขอแล้ว',
  'server.requestsFull': 'คิวคำขอคืนนี้เต็มแล้ว',
  'server.songAlreadyDeleted': 'เพลงนี้อยู่ในถังขยะอยู่แล้ว',
  'server.songNotDeleted': 'เพลงนี้ไม่ได้อยู่ในถังขยะ',
  'server.songRequired': 'เลือกเพลงก่อน',
  'server.inviteInvalid': 'ลิงก์เชิญนี้ใช้ไม่ได้',
  'server.inviteExpired': 'ลิงก์เชิญนี้หมดอายุแล้ว ขอลิงก์ใหม่',
  // MVP-644. Not yet owner-reviewed. คนละเรื่องกับ inviteInvalid/inviteExpired — ลิงก์ไม่ได้ผิดและไม่ได้หมดอายุ
  // แต่ออกไว้ก่อนที่ระบบจะผูกบัญชีกับร้าน ทางแก้มีทางเดียวคือให้คนที่ส่งลิงก์ออกใหม่ ไม่บอกชื่อฟิลด์หรือเส้นทาง
  'server.inviteNoVenue': 'ลิงก์เชิญนี้ออกไว้ก่อนที่ระบบจะผูกบัญชีกับร้าน จึงใช้สมัครไม่ได้แล้ว ให้คนที่ส่งลิงก์มาออกลิงก์ใหม่ให้',
  'server.userExists': 'ชื่อผู้ใช้หรืออีเมลนี้มีคนใช้แล้ว',
  'server.usernameInvalid': 'ตั้งชื่อผู้ใช้อย่างน้อย 3 ตัวอักษร',
  'server.emailInvalid': 'กรอกอีเมลให้ถูกต้อง',
  'server.passwordWeak': 'ตั้งรหัสผ่านอย่างน้อย 10 ตัวอักษร',
  'server.roleMissing': 'เลือกสิทธิ์ให้บัญชีนี้',
  'server.roleInvalid': 'สิทธิ์นี้ไม่มีอยู่บนเซิร์ฟเวอร์แล้ว',
  'server.selfLockout': 'เปลี่ยนแบบนี้แล้วจะล็อกตัวเองออกจากบัญชีแอดมิน',
  // MVP-508. Not yet owner-reviewed. ไม่บอกให้ "ปิดห้องเก่า" เพราะฝั่งเซิร์ฟเวอร์นับห้องที่ปิดแล้วด้วย ปิดไปก็ไม่ว่างเพิ่ม
  'server.roomCapReached': 'ร้านนี้เปิดห้องครบตามที่ไลเซนส์ให้ไว้แล้ว ปิดห้องเก่าก็ไม่ได้ที่ว่างเพิ่ม ถ้าต้องการเพิ่มห้องให้ขอแพ็กเกจที่ใหญ่ขึ้น',
  'server.venueNotInScope': 'บัญชีนี้ดูแลหลายสาขา แต่หน้านี้ยังบอกไม่ได้ว่าจะเพิ่มห้องให้สาขาไหน ให้แอดมินเป็นคนเพิ่มให้',
  // MVP-655. Not yet owner-reviewed.
  'server.venueRequired': 'เลือกสาขาที่ร้านนี้จะดูแลก่อน แล้วค่อยสร้างลิงก์',
  // MVP-635. Not yet owner-reviewed. ใช้คำเดิมของแอป: ร้าน = venue, ห้อง = room (ดู server.roomCapReached, nav.rooms)
  // ไม่แปลประโยคของเซิร์ฟเวอร์ตรง ๆ เพราะประโยคนั้นลงท้ายด้วย POST /api/rooms ซึ่ง KJ ไม่ต้องรู้ — บอกว่าต้องไปขอใคร
  'server.venueHasNoRoom': 'ร้านนี้ยังไม่มีห้อง จึงยังเปิดเพลงไม่ได้ ให้เจ้าของร้านหรือแอดมินเปิดห้องแรกให้ก่อน',
  // MVP-510 slice 3. Not yet owner-reviewed. ใช้คำเดิมของแอป: ร้าน = venue
  // บอกให้ไปหาใคร เพราะ KJ ที่อ่านประโยคนี้ทำอะไรเองไม่ได้
  'server.venueNotTrading': 'ร้านนี้ถูกตั้งเป็นปิดกิจการแล้ว พนักงานจึงเข้าใช้งานไม่ได้ ให้เจ้าของร้านหรือแอดมินเปิดร้านกลับมาก่อน',
  // MVP-636. Not yet owner-reviewed. ต้องบอกว่า "สร้างร้านแล้ว" เพราะร้านถูกสร้างจริง ข้อความรวมเดิมอ่านเหมือนไม่มีอะไรเกิดขึ้น แล้วคนใช้จะกดสร้างซ้ำ
  // สาเหตุจริงอยู่ในล็อกของเซิร์ฟเวอร์ ไม่เอามาใส่ในประโยคนี้
  'server.venueRoomNotCreated': 'สร้างร้านนี้เรียบร้อยแล้ว แต่ยังเปิดห้องแรกให้ไม่สำเร็จ ตอนนี้ยังเปิดเพลงในร้านนี้ไม่ได้จนกว่าจะมีห้อง ให้แอดมินเปิดห้องแรกให้ก่อน',

  // MVP-508: หน้ารายการห้องของเจ้าของร้าน Not yet owner-reviewed.
  // "ห้อง" คือคำที่ทั้งแอปใช้เรียกห้องคาราโอเกะอยู่แล้ว (ดู settings.room) — rail กว้าง 56px จึงเหลือคำเดียว
  'nav.rooms': 'ห้อง',
  'rooms.title': 'ห้องในร้าน',
  'rooms.quota': 'ใช้ไป {used} / {max} ห้อง',
  'rooms.quotaCount': 'มีอยู่ {used} ห้อง',
  'rooms.quotaFull': 'เปิดห้องครบตามไลเซนส์แล้ว',
  'rooms.add': 'เพิ่มห้อง',
  'rooms.addTitle': 'เพิ่มห้องใหม่',
  'rooms.name': 'ชื่อห้อง',
  'rooms.nameRequired': 'ตั้งชื่อห้องก่อน',
  'rooms.nameTooLong': 'ชื่อห้องยาวได้ไม่เกิน {max} ตัวอักษร',
  'rooms.rename': 'เปลี่ยนชื่อ',
  'rooms.renameTitle': 'เปลี่ยนชื่อห้อง {room}',
  'rooms.save': 'บันทึก',
  'rooms.close': 'ปิดห้อง',
  'rooms.reopen': 'เปิดห้องอีกครั้ง',
  'rooms.open': 'เปิดอยู่',
  'rooms.closed': 'ปิดอยู่',
  'rooms.closeConfirm': 'ปิดห้อง {room} ใช่ไหม ห้องนี้จะไม่ขึ้นในรายการให้เลือกอีก ข้อมูลไม่ได้ถูกลบ และเปิดกลับมาได้ที่หน้านี้',
  'rooms.empty': 'ยังไม่มีห้อง',
  'rooms.emptyDetail': 'เพิ่มห้องแรกให้ร้านนี้',
  'rooms.loadError': 'โหลดรายการห้องไม่ได้',
  'rooms.saveError': 'บันทึกการเปลี่ยนแปลงไม่ได้',

  // MVP-509. Not yet owner-reviewed.
  // คำที่ใช้ทั้งบล็อกนี้: ลูกค้า = เจ้าของสัญญา, สาขา = venue, ไลเซนส์ = licence, แพ็กเกจ = tier
  // ไม่บอกให้ "ปิดสาขาเก่า" เพราะฝั่งเซิร์ฟเวอร์นับสาขาที่ปิดแล้วด้วย ปิดไปก็ไม่ว่างเพิ่ม
  'server.venueCapReached':
    'ลูกค้ารายนี้เปิดสาขาครบตามแพ็กเกจแล้ว สาขาที่ปิดไปแล้วยังนับที่นั่งอยู่ ปิดเพิ่มก็ไม่ได้ที่ว่าง ถ้าลูกค้าซื้อสาขาเพิ่มให้ไปเพิ่มจำนวนสาขาในไลเซนส์',
  'server.venueAdminOnly': 'เฉพาะแอดมินเท่านั้นที่เปิดหรือแก้สาขาได้',
  // MVP-641. Not yet owner-reviewed. ใช้คำเดียวกับทั้งบล็อกนี้: ลูกค้า / สาขา / ไลเซนส์ / แพ็กเกจ
  // ฝั่งเซิร์ฟเวอร์มีสองประโยค (ไม่ได้ระบุไลเซนส์ กับ ระบุแล้วแต่ไม่มีอยู่จริง) แต่ส่งโค้ดเดียวและไม่มีตัวแยก
  // จึงรวมเป็นประโยคเดียว ทางแก้บนหน้านี้เหมือนกันทั้งสองแบบคือเลือกลูกค้าจากรายชื่อแล้วบันทึกใหม่
  'server.venueHasNoLicence':
    'ยังไม่ได้สร้างสาขานี้ เพราะยังไม่ได้เลือกไลเซนส์ให้ หรือไลเซนส์ที่เลือกไว้ไม่มีอยู่แล้ว ให้เลือกลูกค้าจากรายชื่อไลเซนส์แล้วบันทึกอีกครั้ง จำนวนสาขาที่เปิดได้มาจากแพ็กเกจของลูกค้า สาขาที่ไม่มีไลเซนส์จึงไม่มีอะไรมาจำกัดและไม่มีใครถูกเก็บเงิน',
  // MVP-570. Not yet owner-reviewed. คำปฏิเสธของเส้นทางนับห้อง คนละประโยคกับด้านบนเพราะเป็นคนละเรื่อง
  // อันนี้แค่อ่าน ไม่ได้แก้อะไรเลย
  'server.venueCountsAdminOnly': 'เฉพาะแอดมินเท่านั้นที่ดูจำนวนห้องของทุกสาขาได้',

  // MVP-645. Not yet owner-reviewed. คนที่อ่านคือคนที่กรอกรหัสผ่านถูกแล้วแต่ยังเข้าไม่ได้ จึงบอกก่อนว่ารหัสผ่านถูก
  // แล้วค่อยบอกว่าบัญชียังไม่เปิด/หมดอายุ และบอกว่าต้องไปหาใคร ไม่มีคำว่า "ลองใหม่" เพราะลองกี่ครั้งก็ไม่ผ่าน
  // ไม่ใส่วันที่ เพราะฝั่งเซิร์ฟเวอร์ส่งมาเป็น ISO timestamp ในประโยคภาษาอังกฤษ ไม่ใช่รูปแบบที่ KJ อ่านได้
  'server.accountNotYetValid': 'รหัสผ่านถูกต้อง แต่บัญชีนี้ยังไม่ถูกเปิดใช้งาน กรุณาแจ้งเจ้าของร้านหรือคนที่ตั้งบัญชีให้เปิดใช้งานก่อน',
  'server.accountExpired': 'รหัสผ่านถูกต้อง แต่บัญชีนี้หมดอายุแล้ว กรุณาแจ้งเจ้าของร้านหรือคนที่ตั้งบัญชีให้ต่ออายุให้',

  // MVP-646 (a). Not yet owner-reviewed. สองประโยคสำหรับสองโค้ดที่ฝั่งเซิร์ฟเวอร์ตั้งใจแยกไว้ — "ห้องปิด" กับ
  // "สาขาถูกปิดทั้งสาขา" ต้องบอกให้ชัดว่าอะไรถูกปิด ไม่งั้นคนคุมจะไปกดเปิด/ปิดห้องทั้งคืนทั้งที่ปัญหาอยู่ที่สาขา
  'server.roomClosed': 'ห้องนี้ปิดอยู่ จึงเปิดรอบในห้องนี้ไม่ได้ ให้ไปเปิดห้องคืนที่หน้าจัดการห้องก่อน',
  'server.venueClosed': 'สาขานี้ถูกปิดทั้งสาขา ไม่ใช่แค่ห้องนี้ จึงเปิดรอบไม่ได้เลย ต้องให้แอดมินเปิดสาขากลับมาก่อน',
  // MVP-646 (b). Not yet owner-reviewed. ใช้เป็นท่อน {reason} ของ ops.request.unacceptFailed เหมือนพี่น้องอีกสามอัน
  // ครึ่งแรกคือข้อเท็จจริงที่ KJ จะไม่มีทางรู้เลยถ้าไม่บอก คือเพลงยังอยู่ในคิวและยังจะเล่นแน่ ครึ่งหลังคือทางออก
  'server.queueEntryNotRemovable': 'เอาเพลงออกจากคิวไม่สำเร็จ เพลงจึงยังอยู่ในคิวและยังจะเล่นอยู่ ให้ไปลบออกที่ช่องคิวแทน',
  // MVP-648 (b). Not yet owner-reviewed. ท่อน {reason} เหมือนกัน มีคนอื่นลบแถวนั้นไปแล้วระหว่างที่หน้าจอยังแสดงอยู่
  // หน้าจอโหลดรายการใหม่ให้เองอยู่แล้ว ประโยคจึงบอกให้ดูรายการใหม่ ไม่ได้บอกให้ลองใหม่
  'server.queueRowGone': 'แถวนั้นไม่อยู่ในรายการแล้ว มีคนอื่นเอาออกไปก่อน ระบบโหลดรายการใหม่ให้แล้ว',

  // MVP-648 (a). Not yet owner-reviewed. ชื่อ slug ของห้องระบบตั้งให้เองจากสาขา ไม่ได้มาจากที่พิมพ์ จึงไม่พูดถึง
  // ทางแก้มีทางเดียวและอยู่บนหน้านี้ คือเปลี่ยนชื่อห้องเก่าที่ไม่ใช้แล้ว
  'server.roomSlugUnavailable': 'สาขานี้ไม่เหลือชื่อห้องว่างให้ตั้งแล้ว ให้เปลี่ยนชื่อห้องเก่าที่ไม่ได้ใช้แล้ว จากนั้นค่อยเพิ่มห้องนี้ใหม่',

  // MVP-509. Not yet owner-reviewed. หน้ารายชื่อลูกค้าและสาขาของผู้ขาย (เฉพาะแอดมิน)
  'nav.licences': 'ไลเซนส์',
  'licences.title': 'ไลเซนส์และสาขา',
  'licences.rosterLabel': 'รายชื่อลูกค้า',
  'licences.detailLabel': 'ลูกค้าที่เลือกอยู่',
  // แถบบอกว่ากำลังทำอะไรกับใคร ห้ามหาย ห้ามปิด และต้องมีชื่อลูกค้านำหน้าชื่อสาขาเสมอ
  'licences.actingOnLabel': 'กำลังทำงานกับ',
  'licences.actingOn.none': 'ยังไม่ได้เลือก — เลือกลูกค้าจากรายการด้านซ้าย',
  'licences.actingOn.licence': '{customer} — ทั้งไลเซนส์',
  'licences.actingOn.venue': '{customer} → {venue} ({slug})',
  'licences.add': 'เพิ่มไลเซนส์',
  'licences.addTitle': 'เพิ่มไลเซนส์ใหม่',
  'licences.edit': 'แก้ไลเซนส์',
  'licences.editTitle': 'แก้ไลเซนส์ของ {customer}',
  'licences.save': 'บันทึก',
  'licences.customerName': 'ชื่อลูกค้า',
  'licences.customerNameRequired': 'ใส่ชื่อลูกค้าก่อน',
  'licences.customerNameTooLong': 'ชื่อลูกค้ายาวได้ไม่เกิน {max} ตัวอักษร',
  'licences.slug': 'รหัสอ้างอิง (slug)',
  'licences.slugRequired': 'ใส่รหัสอ้างอิงก่อน',
  'licences.slugTooLong': 'รหัสอ้างอิงยาวได้ไม่เกิน {max} ตัวอักษร',
  'licences.slugInvalid': 'ใช้ได้เฉพาะ a-z ตัวเลข และขีดกลาง เช่น “siam-karaoke”',
  'licences.tier': 'แพ็กเกจ',
  'licences.tierNone': 'ยังไม่ได้ระบุแพ็กเกจ',
  'licences.maxVenues': 'เปิดได้กี่สาขา',
  'licences.maxRoomsPerVenue': 'แต่ละสาขาเปิดได้กี่ห้อง',
  'licences.capNone': 'ยังไม่ได้กำหนด',
  'licences.numberInvalid': 'ใส่เป็นจำนวนเต็ม หรือเว้นว่างไว้ถ้าไม่จำกัด',
  'licences.dateInvalid': 'ใส่วันที่แบบ ปี-เดือน-วัน (YYYY-MM-DD) หรือเว้นว่างไว้ถ้าไม่มีวันหมดอายุ',
  'licences.expiresAt': 'หมดอายุ',
  'licences.state': 'สถานะ',
  'licences.stateActive': 'ใช้งานอยู่',
  'licences.stateInactive': 'ปิดใช้งาน',
  // MVP-513 บังคับใช้จริงแล้ว หน้านี้จึงอธิบายกฎจริง ไม่ใช่ประโยคเดิมที่ว่า "แสดงเฉยๆ ไม่บังคับ"
  'licences.expiryRule':
    'ก่อนถึงวันนี้ 7 วัน ร้านจะเห็นแถบนับถอยหลังทุกวัน ถึงวันหมดอายุแล้วยังใช้ได้ต่ออีก 7 วัน หลังจากนั้นจะเปิดรอบใหม่ไม่ได้ ถ้าต่อสัญญาแล้วแค่ใส่วันที่ใหม่ให้ไกลขึ้น ระบบก็กลับมาใช้ได้เอง',
  'licences.expiryNone': 'ไม่ได้กำหนดวันหมดอายุ',
  'licences.expiryOk': 'เหลืออีก {days} วัน',
  'licences.expiryEnding': 'อีก {days} วันหมดอายุ',
  'licences.expiryGrace': 'หมดอายุมาแล้ว {days} วัน — ยังใช้งานได้อยู่',
  'licences.expiryLapsed': 'หมดอายุแล้ว — เปิดรอบใหม่ไม่ได้',
  // ช่องโน้ตอยู่ใน Strapi admin panel เท่านั้น เพราะคอลัมน์เป็น private ดึงผ่าน API ไม่ได้เลย
  'licences.notesElsewhere': 'โน้ตส่วนตัวเกี่ยวกับลูกค้ารายนี้ไม่แสดงที่หน้านี้ ต้องอ่านและแก้ใน Strapi admin panel เท่านั้น',
  'licences.venues': 'สาขา',
  'licences.venueQuota': 'ใช้ไป {used} / {max} สาขา',
  'licences.venueQuotaCount': 'มีอยู่ {used} สาขา',
  'licences.venueQuotaFull': 'เปิดสาขาครบตามแพ็กเกจแล้ว',
  'licences.addVenue': 'เพิ่มสาขา',
  'licences.addVenueTitle': 'เพิ่มสาขาให้ {customer}',
  'licences.venueName': 'ชื่อสาขา',
  'licences.venueNameRequired': 'ใส่ชื่อสาขาก่อน',
  'licences.venueNameTooLong': 'ชื่อสาขายาวได้ไม่เกิน {max} ตัวอักษร',
  'licences.venueSlug': 'รหัสอ้างอิงของสาขา (slug)',
  'licences.venueSlugFixed':
    'ใส่ได้ครั้งเดียวตอนนี้ แก้ทีหลังไม่ได้ เพราะทุกหน้าจอใช้ค่านี้ระบุว่าเป็นสาขาไหน และห้องทุกห้องในสาขาก็ใช้ค่านี้ขึ้นต้น',
  'licences.venueOpen': 'เปิดอยู่',
  'licences.venueClosed': 'หยุดแล้ว',
  'licences.selectVenue': 'เลือกสาขานี้',
  'licences.deactivateVenue': 'หยุดสาขานี้',
  'licences.reactivateVenue': 'ให้สาขานี้กลับมาเปิด',

  // MVP-619. "เซลส์" ไม่ใช่ "agency" ในคำที่ผู้ใช้เห็น: คนที่เปิดหน้านี้คือเจ้าของระบบ และคำที่เขา
  // ใช้เรียกคนที่ไปหาลูกค้าคือเซลส์ ส่วน `agency` เป็นชื่อ role ในโค้ด
  'licences.reps': 'เซลส์ที่ดูแลสาขานี้',
  'licences.repsNone': 'ยังไม่มีเซลส์ — สาขานี้เราเปิดให้เอง',
  'licences.repsAdd': '+ เพิ่มเซลส์',
  'licences.repsRemove': 'เอา {name} ออกจากสาขานี้',
  'licences.repsNoAccounts': 'ยังไม่มีบัญชีเซลส์ในระบบ',
  'licences.repsAllAssigned': 'เซลส์ทุกคนอยู่ในสาขานี้แล้ว',
  'licences.repsHint': 'ส่งต่อให้คนใหม่ = เพิ่มชื่อ ไม่ต้องลบคนเดิม คนที่อยู่ในรายชื่อเห็นข้อมูลสาขานี้ได้ เอาออกเมื่อไม่ให้เห็นแล้ว',
  'licences.deactivateVenueConfirm':
    'หยุดสาขา {venue} ({slug}) ใช่ไหม สาขานี้จะเปิดรอบใหม่ไม่ได้จนกว่าจะเปิดกลับ ข้อมูลไม่ได้ถูกลบ ทั้งห้อง บัญชี และประวัติยังอยู่ครบ และย้อนกลับได้ที่หน้านี้',
  // MVP-570. Not yet owner-reviewed. สองประโยคนี้คือกรณี "ยังไม่รู้จำนวนห้อง" — เซิร์ฟเวอร์รุ่นเก่าที่ยังไม่ได้ดีพลอย
  // MVP-570 จะตอบ 404 ต้องบอกตรงๆ ห้ามโชว์เลข 0 หรือเว้นว่าง เพราะทั้งสองอย่างอ่านเป็นตัวเลขที่คนเอาไปใช้ตัดสินใจ
  'licences.venueRoomsCapOnly': 'เปิดได้ไม่เกิน {max} ห้อง — หน้านี้ยังดูไม่ได้ว่าใช้ไปกี่ห้อง',
  'licences.venueRoomsUnknown': 'ไม่ได้จำกัดจำนวนห้อง — หน้านี้ยังดูไม่ได้ว่าใช้ไปกี่ห้อง',
  // MVP-570. Not yet owner-reviewed. ใช้ไปกี่ห้องจากโควตา และสองสถานะที่เป็นหัวใจของสไลซ์นี้
  // ป้ายทั้งสองมีคำของตัวเอง ไม่ได้ใช้แค่สี และ "เกินมา {over} ห้อง" คือประโยคที่ฝ่ายขายพูดทางโทรศัพท์
  'licences.venueRoomsUsed': 'ใช้ไป {used} / {max} ห้อง',
  'licences.venueRoomsCount': 'มี {used} ห้อง — ไม่ได้จำกัดจำนวนห้อง',
  'licences.venueRoomsFull': 'ใช้ห้องครบโควตาแล้ว',
  'licences.venueRoomsOver': 'เกินโควตาห้องมา {over} ห้อง',
  'licences.empty': 'ยังไม่มีไลเซนส์',
  'licences.emptyDetail': 'เพิ่มแพ็กเกจของลูกค้ารายแรก',
  'licences.noVenues': 'ยังไม่มีสาขา',
  'licences.noVenuesDetail': 'เพิ่มสาขาแรกให้ลูกค้ารายนี้',
  'licences.selectPrompt': 'เลือกลูกค้า',
  'licences.selectPromptDetail': 'เลือกจากรายการด้านซ้ายเพื่อดูแพ็กเกจและสาขาของลูกค้ารายนั้น',
  'licences.loadError': 'โหลดรายการไลเซนส์ไม่ได้',
  'licences.saveError': 'บันทึกการเปลี่ยนแปลงไม่ได้',
  // MVP-278: self-serve plans: sign-up, the plans page, the slip queue, their audit rows.
  'nav.plans': 'แพ็กเกจและต่ออายุ',
  'nav.payments': 'ตรวจสลิป',
  'ops.licence.renew': 'ต่ออายุ',
  'auth.login.signup': 'ร้านใหม่? สมัครใช้งาน',
  'auth.signup.subtitle': 'สมัครใช้งานสำหรับร้านใหม่ และเริ่มทดลองใช้ฟรี',
  'auth.signup.shopName': 'ชื่อร้าน',
  'auth.signup.phone': 'เบอร์โทรศัพท์ (ไม่บังคับ)',
  'auth.signup.submit': 'สมัครและเริ่มทดลองใช้',
  'auth.signup.failed': 'สมัครใช้งานไม่สำเร็จ ลองอีกครั้ง',
  'auth.signup.challengeRequired': 'กรุณายืนยันว่าไม่ใช่บอทด้านล่าง แล้วกดสมัครอีกครั้ง',
  'auth.signup.challengeUnavailable': 'ตอนนี้ระบบยืนยันว่าไม่ใช่บอทใช้งานไม่ได้ จึงสมัครไม่ได้ กรุณารอสักครู่แล้วลองอีกครั้ง',
  'auth.signup.goToSignIn': 'ไปหน้าเข้าสู่ระบบ',
  'auth.signup.haveAccount': 'มีบัญชีแล้ว? เข้าสู่ระบบ',
  'server.shopNameInvalid': 'กรอกชื่อร้าน 2 ถึง 120 ตัวอักษร',
  'server.phoneInvalid': 'กรอกเบอร์โทรศัพท์ไทยให้ถูกต้อง หรือเว้นว่างไว้',
  'server.trialAlreadyUsed': 'อีเมลหรือเบอร์โทรศัพท์นี้เคยใช้สิทธิ์ทดลองใช้ฟรีไปแล้ว',
  'server.trialUnavailable': 'ตอนนี้ยังเปิดรับสมัครไม่ได้ กรุณาลองใหม่ภายหลัง',
  'server.licenceRequired': 'บัญชีนี้ไม่มีไลเซนส์ให้ชำระเงิน',
  'server.planNotFound': 'ไม่พบแพ็กเกจนี้ หรือแพ็กเกจนี้ปิดขายแล้ว',
  'server.planNotPurchasable': 'แพ็กเกจทดลองใช้ฟรีซื้อไม่ได้',
  'server.planNotPriced': 'แพ็กเกจนี้ยังไม่เปิดขาย',
  'server.slipRequired': 'แนบสลิปการโอนเงินก่อน',
  'server.slipInvalid': 'สลิปต้องเป็นรูปภาพหรือไฟล์ PDF',
  'server.slipTooLarge': 'ไฟล์สลิปใหญ่เกินไป ต้องไม่เกิน 10 MB',
  'server.slipDuplicate': 'ส่งสลิปนี้ไปแล้ว รอการตรวจสอบ',
  'server.licenceNotSelfServe': 'ไลเซนส์นี้ต่ออายุผ่านผู้ดูแลระบบ ไม่ใช่ที่หน้านี้',
  'server.emailNotConfirmed': 'กรุณายืนยันอีเมลก่อน เปิดลิงก์ที่เราส่งไปให้ หรือกดส่งอีกครั้งด้านล่าง',
  'server.emailSendFailed': 'สร้างบัญชีแล้ว แต่ส่งอีเมลยืนยันไม่สำเร็จ กรุณากด "ส่งอีเมลอีกครั้ง"',
  'server.signupPending': 'อีเมลนี้สมัครไว้แล้วและรอการยืนยัน ตรวจสอบกล่องจดหมาย หรือกดส่งอีเมลอีกครั้ง',
  'server.confirmInvalid': 'ลิงก์ยืนยันนี้ใช้ไม่ได้ อาจถูกใช้ไปแล้ว ลองเข้าสู่ระบบดู',
  'server.confirmExpired': 'ลิงก์ยืนยันนี้หมดอายุแล้ว เข้าสู่ระบบด้วยอีเมลและรหัสผ่านเพื่อขอลิงก์ใหม่',
  'server.originUnknown': 'กรุณาสมัครจากเว็บไซต์ RMS Karaoke',
  'server.bankAccountIncomplete': 'กรอกธนาคาร ชื่อบัญชี และเลขที่บัญชีให้ครบทั้งสามช่อง หรือเว้นว่างทั้งหมด',
  'server.bankAccountNoInvalid': 'เลขที่บัญชีต้องเป็นตัวเลข 10 ถึง 15 หลัก (ใส่ขีดหรือเว้นวรรคได้)',
  'server.promptPayIdInvalid': 'พร้อมเพย์ต้องเป็นเบอร์มือถือ 10 หลัก หรือเลขประจำตัวผู้เสียภาษี 13 หลัก',
  'auth.checkEmail.title': 'ตรวจสอบอีเมลของคุณ',
  'auth.checkEmail.body': 'เราส่งลิงก์ยืนยันไปที่ {email} แล้ว เปิดลิงก์ภายใน 24 ชั่วโมงเพื่อเริ่มทดลองใช้ฟรี',
  'auth.checkEmail.resend': 'ส่งอีเมลอีกครั้ง',
  'auth.checkEmail.resent': 'ส่งแล้ว ถ้าไม่ได้รับภายในไม่กี่นาที ลองดูในโฟลเดอร์จดหมายขยะ',
  'auth.checkEmail.resendFailed': 'ส่งไม่สำเร็จ ลองอีกครั้งในอีกสักครู่',
  'auth.confirm.checking': 'กำลังยืนยันอีเมล…',
  'auth.confirm.done': 'ยืนยันอีเมลแล้ว เริ่มทดลองใช้ฟรี 7 วันแล้ว เข้าสู่ระบบด้วยอีเมลและรหัสผ่านของคุณ',
  'auth.confirm.failed': 'ยืนยันอีเมลไม่สำเร็จ ลองอีกครั้งในอีกสักครู่',
  'server.signupClosed': 'ขณะนี้ยังไม่เปิดรับสมัคร',
  'server.adminOnly': 'เฉพาะผู้ดูแลระบบเท่านั้นที่ตรวจสลิปได้',
  'server.paymentIncomplete': 'รายการนี้ไม่มีไลเซนส์หรือแพ็กเกจให้ต่ออายุ',
  'server.paymentAlreadyDecided': 'สลิปนี้มีคนตรวจไปแล้ว โหลดรายการใหม่ให้แล้ว',
  'server.noteRequired': 'กรอกเหตุผลที่สลิปไม่ผ่าน ร้านจะเห็นข้อความนี้',
  'plans.expires': 'ใช้งานได้ถึง {date}',
  'plans.loadError': 'โหลดแพ็กเกจไม่ได้',
  'plans.days': '{days} วัน',
  'plans.limits': '{venues} สาขา สาขาละ {rooms} ห้อง',
  'plans.price': '{price} บาท',
  'plans.notOnSale': 'ยังไม่เปิดขาย',
  'plans.trial': 'ทดลองใช้ฟรี',
  'plans.choose': 'เลือกแพ็กเกจนี้',
  'plans.pay.heading': '{plan}: โอน {price} ผ่าน PromptPay',
  'plans.pay.steps': 'สแกน QR ด้วยแอปธนาคาร โอนตามยอดนี้พอดี แล้วแนบสลิปด้านล่าง',
  'plans.pay.bankSteps': 'โอนเงินเข้าบัญชีนี้ตามยอดพอดี แล้วแนบสลิปด้านล่าง',
'plans.pay.bankName': 'ธนาคาร',
'plans.pay.bankAccountName': 'ชื่อบัญชี',
'plans.pay.bankAccountNo': 'เลขที่บัญชี',
'plans.pay.amount': 'ยอดที่ต้องโอน',
'plans.pay.copy': 'คัดลอก',
'plans.pay.notConfigured': 'ยังไม่ได้ตั้งค่าบัญชีรับเงิน',
  'plans.pay.slip': 'สลิปการโอนเงิน (รูปภาพหรือ PDF)',
  'plans.pay.submit': 'ส่งสลิป',
  'plans.pay.sent': 'ส่งสลิปแล้ว รอผู้ดูแลระบบตรวจ',
  'plans.pay.failed': 'ส่งสลิปไม่สำเร็จ',
  'plans.history.title': 'ประวัติการชำระเงิน',
  'plans.history.empty': 'ยังไม่มีการชำระเงิน',
  'plans.history.emptyDetail': 'เลือกแพ็กเกจด้านบนเพื่อต่ออายุ',
  'plans.status.pending': 'รอตรวจ',
  'plans.status.approved': 'อนุมัติแล้ว',
  'plans.status.rejected': 'ไม่ผ่าน',
  'plans.reason': 'เหตุผล: {note}',
  'payments.filter': 'สถานะสลิป',
  'payments.filterAll': 'ทั้งหมด',
  'payments.loadError': 'โหลดรายการสลิปไม่ได้',
  'payments.empty': 'ไม่มีรายการ',
  'payments.emptyDetail': 'สลิปที่ร้านส่งมาจะแสดงที่นี่',
  'payments.openSlip': 'เปิดสลิป (PDF)',
  'payments.slipAlt': 'สลิปการโอนเงินของ {shop}',
  'payments.noSlip': 'ไม่มีสลิป',
  'payments.viewSlip': 'ดูสลิป',
  'payments.slipLoadError': 'เปิดสลิปไม่สำเร็จ ลองอีกครั้ง',
  'payments.unknownShop': 'ไม่ทราบชื่อร้าน',
  'payments.sentAt': 'ส่งเมื่อ {date}',
  'payments.expires': 'ไลเซนส์หมดอายุ {date}',
  'payments.note': 'เหตุผล (ต้องกรอกเมื่อไม่ผ่าน)',
  'payments.approve': 'อนุมัติ',
  'payments.reject': 'ไม่ผ่าน',
  'payments.approved': 'อนุมัติแล้ว ต่ออายุไลเซนส์ให้ร้านแล้ว',
  'payments.rejected': 'บันทึกว่าไม่ผ่านแล้ว ร้านจะเห็นเหตุผล',
  'payments.actionFailed': 'บันทึกผลการตรวจไม่สำเร็จ',
  'payments.decidedBy': 'ตรวจโดย {name} เมื่อ {date}',
  'admin.audit.action.signupTrial': 'สมัครทดลองใช้',
  'admin.audit.action.paymentApprove': 'อนุมัติสลิป',
  'admin.audit.action.paymentReject': 'สลิปไม่ผ่าน',
  'admin.audit.action.paymentAccountUpdate': 'เปลี่ยนบัญชีรับเงิน',
  'admin.audit.signupTrial': 'สมัครร้านใหม่และเริ่มทดลองใช้ฟรี ({target})',
  'admin.audit.paymentApprove': 'อนุมัติสลิปของ {target} ไลเซนส์ใช้ได้ถึง {to}',
  'admin.audit.paymentReject': 'สลิปของ {target} ไม่ผ่าน: {note}',
  'admin.audit.paymentAccountUpdate': 'เปลี่ยนบัญชีที่ร้านค้าโอนเงินเข้า',
};
